import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { combineFilters, getFilterOverlay } from "../utils/liveFilters";
import ThemeSelector from "../components/ThemeSelector";
import LanguageSelector from "../components/LanguageSelector";
import FullscreenCamera from "../components/FullscreenCamera";
import VoiceCommands from "../components/VoiceCommands";
import StripePayment from "../components/StripePayment";
import GestureControls from "../components/GestureControls";
import StarMap from "../components/StarMap";
import ARConstellationOverlay, { ARControls } from "../components/ARConstellationOverlay";
import NightSkyAlerts from "../components/NightSkyAlerts";
import GuidedTour, { useTourState } from "../components/GuidedTour";
import SkyThemeBackground from "../components/SkyThemeBackground";
import { resolveSkyTheme, allThemeKeys, publicThemeKeys } from "../data/skyThemes";
import { 
  Camera, 
  Upload, 
  Scan, 
  Sun, 
  Moon, 
  Star, 
  Sparkles, 
  Eye, 
  Aperture,
  X,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Zap,
  Crown,
  RefreshCw,
  Download,
  Image as ImageIcon,
  Loader2,
  Settings,
  History,
  CreditCard,
  CheckCircle,
  Share2,
  Copy,
  Twitter,
  Facebook,
  Instagram,
  Mail,
  Lock,
  Unlock,
  ZoomIn,
  ZoomOut,
  Telescope,
  Minus,
  Plus,
  Video,
  Film,
  Play,
  Pause,
  Palette,
  Thermometer,
  Radio,
  Focus,
  Target,
  Home,
  ArrowLeft,
  RotateCcw,
  Menu,
  ShoppingCart,
  Building2,
  DollarSign,
  Globe,
  MapPin,
  Compass,
  Activity,
  Cpu,
  Maximize,
  Languages,
  Mic,
  Hand,
  Map,
  EyeOff,
  Bell,
  SlidersHorizontal
} from "lucide-react";
import ViewfinderOverlay from "../components/ViewfinderOverlay";
import DownloadAppButton, { FloatingInstallBanner } from "../components/DownloadAppButton";
import Pro3DControlPanel from "../components/HUDControlPanel";
import ProActionBar from "../components/HUDActionBar";
import IPhoneCameraControls from "../components/iPhoneCameraControls";
import { trackShare, trackPageView, trackPremiumClick } from "../utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Owner secret code - change this to your own secret!
const OWNER_SECRET = "Aether2025";

// Compact Filter Component
const FilterChip = ({ icon: Icon, label, active, locked, onClick, color = "#00f2ea" }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium transition-all ${
      active 
        ? 'ring-2 ring-offset-1 ring-offset-transparent' 
        : 'opacity-70 hover:opacity-100'
    } ${locked ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-105'}`}
    style={{
      background: active ? `${color}20` : 'rgba(255,255,255,0.05)',
      color: active ? color : '#94a3b8',
      ringColor: active ? color : 'transparent'
    }}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
    {locked && <Lock className="w-3 h-3 ml-1" />}
  </button>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, currentTheme } = useTheme();
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // State
  const [mode, setMode] = useState("camera"); // camera, upload, video
  const prevModeRef = useRef("camera");
  const [stream, setStream] = useState(null);
  const [isCameraPaused, setIsCameraPaused] = useState(false); // Play/Pause camera feed

  // Guided Tour
  const { showTour, startTour, endTour } = useTourState();

  // Allow deep-linking into the tour from Landing (/dashboard?tour=1)
  useEffect(() => {
    if (searchParams.get('tour') === '1') {
      startTour();
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [enhancedImage, setEnhancedImage] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  // Premium status - verified via device fingerprint
  const [isPremium, setIsPremium] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Check premium status on load via device fingerprint
  useEffect(() => {
    const checkPremium = async () => {
      try {
        // Dynamic import to avoid SSR issues
        const { getDeviceFingerprint } = await import('../utils/deviceFingerprint');
        const fp = await getDeviceFingerprint();
        
        const response = await axios.get(`${API}/payment/check-premium?device_fingerprint=${fp}`);
        if (response.data.is_premium) {
          setIsPremium(true);
          localStorage.setItem('skybot_premium', 'true');
          localStorage.setItem('skybot_premium_plan', response.data.plan || 'unknown');
        }
      } catch (err) {
        // Check localStorage as fallback
        const stored = localStorage.getItem('skybot_premium');
        if (stored === 'true') {
          setIsPremium(true);
        }
      }
    };
    checkPremium();
  }, []);
  
  // Function to activate premium and persist
  const activatePremium = useCallback(() => {
    setIsPremium(true);
    localStorage.setItem('skybot_premium', 'true');
    localStorage.setItem('skybot_premium_date', new Date().toISOString());
    toast.success("🎉 Premium activated! Enjoy all features!", { duration: 5000 });
  }, []);

  // Function to deactivate premium (for testing/admin)
  const deactivatePremium = useCallback(() => {
    setIsPremium(false);
    localStorage.removeItem('skybot_premium');
    localStorage.removeItem('skybot_premium_date');
    localStorage.removeItem('skybot_premium_plan');
    toast.info("Premium deactivated");
  }, []);

  // ===== Cohesive Sky Theme (mirrors the Landing Page 8K backdrop) =====
  const [currentSkyTheme, setCurrentSkyTheme] = useState(() => {
    const saved = localStorage.getItem('skybot_active_sky_theme');
    if (saved) return saved;
    return publicThemeKeys[Math.floor(Math.random() * publicThemeKeys.length)];
  });
  const activeSkyTheme = resolveSkyTheme(currentSkyTheme);

  // Auto-rotate the backdrop every 30s, matching the landing page rhythm
  useEffect(() => {
    const pool = (isOwner || isPremium) ? allThemeKeys : publicThemeKeys;
    const interval = setInterval(() => {
      setCurrentSkyTheme((prev) => {
        const idx = pool.indexOf(prev);
        const next = pool[(idx + 1) % pool.length];
        localStorage.setItem('skybot_active_sky_theme', next);
        return next;
      });
    }, 30000);
    return () => clearInterval(interval);
  }, [isOwner, isPremium]);

  // Live AI (Analyze button toggle)
  const [isLiveAIOn, setIsLiveAIOn] = useState(false);
  const liveAIIntervalRef = useRef(null);
  const prevFrameRef = useRef(null);
  const [motionHeatmapDataUrl, setMotionHeatmapDataUrl] = useState(null);
  const [lastLiveAIResult, setLastLiveAIResult] = useState(null);

  // AI Anomaly Tracking State
  const [isAnomalyTrackingOn, setIsAnomalyTrackingOn] = useState(false);
  const anomalyTrackingRef = useRef(null);
  const [trackedAnomalies, setTrackedAnomalies] = useState([]);
  const [anomalyOverlayUrl, setAnomalyOverlayUrl] = useState(null);
  const [trackingSensitivity, setTrackingSensitivity] = useState(0.5);
  const [frameAnalysis, setFrameAnalysis] = useState("");
  const previousAnomaliesRef = useRef([]);

  // AI Zoom Clarity State
  const [aiClarityEnabled, setAiClarityEnabled] = useState(true);
  const [clarityDetails, setClarityDetails] = useState(null);
  const [isApplyingClarity, setIsApplyingClarity] = useState(false);

  // Fullscreen State
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [ownerCode, setOwnerCode] = useState("");
  const [paymentCode, setPaymentCode] = useState("");
  const [paymentCodeError, setPaymentCodeError] = useState("");

  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  // Verify payment code via API
  const verifyPaymentCode = async () => {
    const code = paymentCode.toUpperCase().trim();
    if (!code) {
      setPaymentCodeError("Please enter a code");
      return;
    }

    // Also allow owner code
    if (code === 'AETHER2025') {
      activatePremium();
      setShowPaymentModal(false);
      setPaymentCode("");
      setPaymentCodeError("");
      return;
    }

    setIsVerifyingCode(true);
    setPaymentCodeError("");

    try {
      const response = await axios.post(`${API}/payment/verify-code`, { code });
      
      if (response.data.valid) {
        activatePremium();
        setShowPaymentModal(false);
        setPaymentCode("");
        setPaymentCodeError("");
      } else {
        setPaymentCodeError(response.data.message || "Invalid code");
      }
    } catch (error) {
      console.error("Code verification error:", error);
      setPaymentCodeError("Failed to verify code. Please try again.");
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const [selectedPlan, setSelectedPlan] = useState(null);

  // Responsive guard (do not rely solely on CSS breakpoints for critical layout)
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  
  // Video Analysis State
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [videoInfo, setVideoInfo] = useState(null);
  const [videoAnalysisResult, setVideoAnalysisResult] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  
  // Ultra Zoom State (Premium Feature - native camera X-Zoom)
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showZoomControls, setShowZoomControls] = useState(false);
  const [zoomQualityMode, setZoomQualityMode] = useState('standard');
  const MAX_ZOOM = 15; // Native camera zoom only (matches iPhone typical max)

  // Camera Switching State
  const [facingMode, setFacingMode] = useState('environment'); // 'user' or 'environment'
  const [availableCameras, setAvailableCameras] = useState([]);
  const [cameraModules, setCameraModules] = useState([]); // Detected camera modules with capabilities
  const [activeCameraModule, setActiveCameraModule] = useState(null); // Current active module

  // Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);

  // Timelapse State
  const [isTimelapseMode, setIsTimelapseMode] = useState(false);
  const [timelapseSessionId, setTimelapseSessionId] = useState(null);
  const [timelapseFrameCount, setTimelapseFrameCount] = useState(0);
  const [timelapseInterval, setTimelapseInterval] = useState(null);
  const [timelapseVideo, setTimelapseVideo] = useState(null);

  // Filter Panel State
  const [showFreeFilters, setShowFreeFilters] = useState(true);
  const [showPremiumFilters, setShowPremiumFilters] = useState(true);
  
  // Solar Filter Intensity Level
  const [solarLevel, setSolarLevel] = useState('medium');

  // Moon Mode State
  const [isMoonModeActive, setIsMoonModeActive] = useState(false);
  const moonModeDefaultZoom = useRef(1); // Store zoom before moon mode

  // Meteor Watch Mode State
  const [isMeteorModeActive, setIsMeteorModeActive] = useState(false);
  const meteorModeDefaultZoom = useRef(1);

  // ISS Tracker Mode State
  const [isISSModeActive, setIsISSModeActive] = useState(false);
  const issModeDefaultZoom = useRef(1);

  // AI Video Ad Generation State
  const [showAdGeneratorModal, setShowAdGeneratorModal] = useState(false);
  const [adPrompt, setAdPrompt] = useState("A stunning showcase of the night sky with stars, meteors, and mysterious lights captured by Skybot - the ultimate sky anomaly detection app");
  const [adDuration, setAdDuration] = useState(4);
  const [adSize, setAdSize] = useState("1280x720");
  const [isGeneratingAd, setIsGeneratingAd] = useState(false);
  const [generatedAdVideo, setGeneratedAdVideo] = useState(null);

  // Commercial/Purchase Inquiry State
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseInquiry, setPurchaseInquiry] = useState({
    name: '',
    email: '',
    company: '',
    inquiry_type: 'license',
    message: '',
    budget_range: ''
  });
  const [isSubmittingInquiry, setIsSubmittingInquiry] = useState(false);

  // Celestial Overlay State
  const [showCelestialOverlay, setShowCelestialOverlay] = useState(false);
  const [celestialData, setCelestialData] = useState(null);

  // Real-time Analysis State
  const [isRealTimeMode, setIsRealTimeMode] = useState(false);
  const [realTimeAnalysis, setRealTimeAnalysis] = useState(null);
  const [realTimeInterval, setRealTimeInterval] = useState(null);

  // Quick Cash App Payment Modal
  const [showQuickPayment, setShowQuickPayment] = useState(false);

  // Capture Analysis Prompt State
  const [showAnalyzePrompt, setShowAnalyzePrompt] = useState(false);
  const [pendingCaptureForAnalysis, setPendingCaptureForAnalysis] = useState(null);

  // Gesture Controls State
  const cameraContainerRef = useRef(null);
  const [gesturesEnabled, setGesturesEnabled] = useState(true); // Enable by default

  // Screen Adjustments Slide-out Panel
  const [showAdjustments, setShowAdjustments] = useState(false);
  const [screenAdjustments, setScreenAdjustments] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    exposure: 50
  });

  // Star Map State
  const [showStarMap, setShowStarMap] = useState(false);

  // AR Constellation Overlay State
  const [arEnabled, setArEnabled] = useState(false);
  const [arShowLabels, setArShowLabels] = useState(true);
  const [arShowLines, setArShowLines] = useState(true);
  const [arShowPlanets, setArShowPlanets] = useState(true);

  // Night Sky Alerts State
  const [showNightSkyAlerts, setShowNightSkyAlerts] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    // Basic filters
    sun_dimming: false,
    night_mode: false,
    noise_reduction: false,
    // Solar Eclipse modes
    solar_eclipse: false,
    solar_eclipse_totality: false,
    solar_eclipse_partial: false,
    solar_corona: false,
    solar_prominences: false,
    // Lunar Eclipse modes
    lunar_eclipse: false,
    lunar_eclipse_blood_moon: false,
    lunar_eclipse_penumbral: false,
    lunar_surface: false,
    // Premium filters
    star_enhancement: false,
    deep_sky: false,
    anomaly_highlight: false,
    "4k_upscale": false,
    "8k_ultra": false,
    night_vision: false,
    night_vision_white: false,
    starlight: false,
    infrared: false,
    thermal: false,
    thermal_ironbow: false,
    thermal_rainbow: false,
    motion_enhance: false,
    auto_clarity: false,
    object_tracking: false,
    ufo_track: false,
    meteor_track: false,
    planet_enhance: false,
    hdr_enhance: false
  });

  // Check for owner status on mount
  useEffect(() => {
    const ownerStatus = localStorage.getItem('aether_owner');
    if (ownerStatus === 'true') {
      setIsOwner(true);
      localStorage.setItem('skybot_owner_mode', 'true');
      activatePremium();
    }
    // Track page view once on mount
    trackPageView('/dashboard');
  }, [activatePremium]);

  // Track premium click when payment modal opens
  useEffect(() => {
    if (showQuickPayment) {
      trackPremiumClick();
    }
  }, [showQuickPayment]);

  // Poll payment status
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please check your email for confirmation.");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        activatePremium();
        return;
      } else if (response.data.status === 'expired') {
        toast.error("Payment session expired. Please try again.");
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error("Payment status check error:", error);
      toast.error("Error verifying payment");
    }
  }, [activatePremium]);

  // Check for payment return
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      pollPaymentStatus(sessionId);
      setSearchParams({});
    } else if (payment === 'cancelled') {
      toast.error("Payment was cancelled");
      setSearchParams({});
    }
  }, [searchParams, pollPaymentStatus, setSearchParams]);



  // Owner login
  const handleOwnerLogin = () => {
    if (ownerCode === OWNER_SECRET) {
      setIsOwner(true);
      setIsPremium(true);
      localStorage.setItem('aether_owner', 'true');
      localStorage.setItem('skybot_owner_mode', 'true');
      setShowOwnerModal(false);
      toast.success("Owner access granted! All features unlocked.");
    } else {
      toast.error("Invalid code");
    }
  };

  // Handle upgrade click - show email modal first
  const handleUpgradeClick = (plan) => {
    setSelectedPlan(plan);
    setShowEmailModal(true);
  };

  // After email collected, proceed to payment
  const handleEmailSubmit = async () => {
    if (!userEmail || !userEmail.includes('@')) {
      toast.error("Please enter a valid email");
      return;
    }

    // Save email to backend
    try {
      await axios.post(`${API}/subscribe`, { email: userEmail, plan: selectedPlan });
    } catch (e) {
      console.log("Email save error:", e);
    }

    setShowEmailModal(false);
    
    if (selectedPlan === 'cashapp_monthly') {
      window.open('https://cash.app/$AetherLens/9.99', '_blank');
      toast.success("Complete payment in Cash App, then click 'I've Paid'");
    } else if (selectedPlan === 'cashapp_yearly') {
      window.open('https://cash.app/$AetherLens/99.99', '_blank');
      toast.success("Complete payment in Cash App, then click 'I've Paid'");
    } else {
      handleUpgrade(selectedPlan);
    }
  };

  // Handle premium upgrade
  const handleUpgrade = async (packageId = 'monthly') => {
    setIsProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: packageId,
        origin_url: window.location.origin,
        email: userEmail
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to create checkout session");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Share functionality
  const shareUrl = window.location.origin;
  const shareText = "I found something CRAZY in the sky with Skybot! AI-powered sky anomaly detection. See what's really up there.";
  
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
    reddit: `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard!");
  };

  const activeFilters = Object.entries(filters)
    .filter(([_, active]) => active)
    .map(([name]) => name);

  // Live filter styles for camera feed - instant preview with solar level support
  const liveFilterStyle = useMemo(() => {
    return combineFilters(activeFilters, solarLevel);
  }, [activeFilters, solarLevel]);

  // Filter overlays (scan lines, vignettes, etc.)
  const filterOverlays = useMemo(() => {
    return getFilterOverlay(activeFilters, theme.colors);
  }, [activeFilters, theme.colors]);

  // ULTRA 4K 240Hz Camera Configuration
  const CAMERA_CONFIG = {
    // Ultra 4K Resolution (3840x2160 or higher)
    width: { ideal: 4096, min: 1920 },
    height: { ideal: 2160, min: 1080 },
    // 240Hz Frame Rate for ultra smooth footage
    frameRate: { ideal: 240, min: 60 },
    // Advanced settings
    facingMode: "environment",
    // Request highest quality
    aspectRatio: { ideal: 16/9 },
    // Disable auto features for manual control
    autoGainControl: false,
    noiseSuppression: false,
    // High dynamic range if available
    // resizeMode: "none" // Prevents browser downscaling
  };

  // Start camera with ULTRA HIGH resolution and camera selection
  // Enhanced: Detects multiple camera modules for smart zoom switching
  const startCamera = useCallback(async (preferredFacing = facingMode, specificDeviceId = null) => {
    console.log('🎥 Starting camera with facing mode:', preferredFacing, specificDeviceId ? `Device: ${specificDeviceId}` : '');
    
    // Check if mediaDevices is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Camera not supported on this browser. Please use a modern browser.");
      return;
    }
    
    try {
      let mediaStream;
      
      // Build constraints - use specific device if provided (for module switching)
      const constraints = {
        video: specificDeviceId 
          ? {
              deviceId: { exact: specificDeviceId },
              width: { ideal: 4096 },
              height: { ideal: 2160 },
              frameRate: { ideal: 60 }
            }
          : {
              facingMode: preferredFacing,
              width: { ideal: 4096 },
              height: { ideal: 2160 },
              frameRate: { ideal: 60 }
            },
        audio: false
      };

      console.log('🎥 Requesting camera with constraints:', JSON.stringify(constraints));

      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        // Fallback to basic constraints for mobile/older devices
        console.log('Advanced constraints failed, using basic:', e);
        const fallbackConstraints = specificDeviceId 
          ? { video: { deviceId: { exact: specificDeviceId } }, audio: false }
          : { video: { facingMode: preferredFacing }, audio: false };
        mediaStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
      }
      
      // Enumerate devices and detect camera modules
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        setAvailableCameras(videoDevices);
        console.log('📷 Available cameras:', videoDevices.length);
        
        // Detect camera modules (wide, ultrawide, telephoto) based on labels
        const modules = await detectCameraModules(videoDevices, preferredFacing);
        setCameraModules(modules);
        console.log('📸 Detected camera modules:', modules);
      } catch (e) {
        console.log('enumerateDevices failed:', e);
      }

      setStream(mediaStream);
      setFacingMode(preferredFacing);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        
        // Log actual resolution achieved
        const track = mediaStream.getVideoTracks()[0];
        const settings = track.getSettings();
        const capabilities = track.getCapabilities?.() || {};
        
        console.log(`📹 SKYBOT ULTRA CAM: ${settings.width}x${settings.height} @ ${settings.frameRate}fps`);
        console.log(`📷 Camera: ${track.label}`);
        console.log(`🔍 Zoom capabilities:`, capabilities.zoom);
        
        // Determine module type from label
        const moduleType = identifyModuleType(track.label);
        setActiveCameraModule({
          deviceId: settings.deviceId,
          label: track.label,
          type: moduleType,
          width: settings.width,
          height: settings.height,
          zoomCapabilities: capabilities.zoom || null
        });
        
        // Store zoom capabilities for optical zoom
        if (capabilities.zoom) {
          window.cameraZoomCapabilities = capabilities.zoom;
          window.cameraTrack = track;
          console.log(`🔭 Optical zoom range: ${capabilities.zoom.min}x - ${capabilities.zoom.max}x`);
        } else {
          window.cameraZoomCapabilities = null;
          window.cameraTrack = track;
        }
        
        // Calculate megapixels
        const megapixels = ((settings.width * settings.height) / 1000000).toFixed(1);
        const moduleLabel = moduleType ? ` [${moduleType}]` : '';
        toast.success(`${settings.width}x${settings.height} (${megapixels}MP)${moduleLabel}`);
      }
    } catch (err) {
      console.warn("Camera unavailable:", err?.name, err?.message);
      
      // Provide specific error messages based on the error type
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        toast.error("Camera permission denied. Please allow camera access in your browser settings and refresh the page.");
      } else if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
        toast.error("No camera found. Please connect a camera and try again.");
      } else if (err?.name === 'NotReadableError' || err?.name === 'TrackStartError') {
        toast.error("Camera is in use by another app. Please close other apps using the camera.");
      } else if (err?.name === 'OverconstrainedError') {
        toast.error("Camera doesn't support the required settings. Trying basic mode...");
        // Try one more time with absolutely minimal constraints
        try {
          const basicStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          setStream(basicStream);
          if (videoRef.current) {
            videoRef.current.srcObject = basicStream;
          }
          toast.success("Camera started in basic mode");
          return;
        } catch (basicErr) {
          console.error("Basic camera also failed:", basicErr);
        }
      } else {
        toast.error("Could not access camera. Please grant permission or use upload mode.");
      }
    }
  }, [facingMode]);

  // Identify camera module type from label
  const identifyModuleType = (label) => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes('ultra') || lowerLabel.includes('wide') && lowerLabel.includes('0.')) {
      return 'ultrawide';
    } else if (lowerLabel.includes('tele') || lowerLabel.includes('zoom') || lowerLabel.includes('2x') || lowerLabel.includes('3x') || lowerLabel.includes('5x') || lowerLabel.includes('10x')) {
      return 'telephoto';
    } else if (lowerLabel.includes('wide') || lowerLabel.includes('main') || lowerLabel.includes('back')) {
      return 'wide';
    } else if (lowerLabel.includes('front') || lowerLabel.includes('selfie')) {
      return 'front';
    }
    return 'main';
  };

  // Detect available camera modules and their optimal zoom ranges
  const detectCameraModules = async (videoDevices, facing) => {
    const modules = [];
    
    // Filter rear cameras only for module detection
    const rearCameras = videoDevices.filter(d => {
      const label = d.label.toLowerCase();
      return facing === 'environment' 
        ? !label.includes('front') && !label.includes('selfie')
        : label.includes('front') || label.includes('selfie');
    });
    
    for (const device of rearCameras) {
      const moduleType = identifyModuleType(device.label);
      
      // Assign optimal zoom ranges based on module type
      let optimalZoomRange = { min: 1, max: 2 };
      switch (moduleType) {
        case 'ultrawide':
          optimalZoomRange = { min: 0.5, max: 1 };
          break;
        case 'wide':
        case 'main':
          optimalZoomRange = { min: 1, max: 3 };
          break;
        case 'telephoto':
          optimalZoomRange = { min: 2, max: 10 };
          break;
        default:
          optimalZoomRange = { min: 1, max: 5 };
      }
      
      modules.push({
        deviceId: device.deviceId,
        label: device.label,
        type: moduleType,
        optimalZoomRange
      });
    }
    
    // Sort by optimal min zoom (ultrawide first, then wide, then telephoto)
    modules.sort((a, b) => a.optimalZoomRange.min - b.optimalZoomRange.min);
    
    return modules;
  };

  // Find the optimal camera module for a given zoom level
  const getOptimalModule = useCallback((targetZoom) => {
    if (cameraModules.length === 0) return null;
    
    // Find the module whose optimal range best covers the target zoom
    for (const module of cameraModules) {
      if (targetZoom >= module.optimalZoomRange.min && targetZoom <= module.optimalZoomRange.max) {
        return module;
      }
    }
    
    // If no exact match, find the closest module
    // For high zoom, prefer telephoto; for low zoom, prefer ultrawide
    if (targetZoom > 3) {
      const telephoto = cameraModules.find(m => m.type === 'telephoto');
      if (telephoto) return telephoto;
    }
    if (targetZoom < 1) {
      const ultrawide = cameraModules.find(m => m.type === 'ultrawide');
      if (ultrawide) return ultrawide;
    }
    
    // Default to wide/main camera
    return cameraModules.find(m => m.type === 'wide' || m.type === 'main') || cameraModules[0];
  }, [cameraModules]);

  // Native camera zoom ONLY - mimics iPhone camera behavior
  const applyZoom = useCallback(async (newZoomLevel) => {
    const track = window.cameraTrack;
    const capabilities = window.cameraZoomCapabilities;
    
    if (track && capabilities) {
      const opticalMax = capabilities.max || 10;
      const opticalMin = capabilities.min || 1;
      
      // Clamp to device's native zoom range
      const nativeZoom = Math.min(opticalMax, Math.max(opticalMin, newZoomLevel));
      
      try {
        await track.applyConstraints({ advanced: [{ zoom: nativeZoom }] });
        console.log(`📷 Native zoom: ${nativeZoom}x (device range: ${opticalMin}x - ${opticalMax}x)`);
        setZoomLevel(nativeZoom);
      } catch (e) {
        console.log('Native zoom not supported:', e);
        setZoomLevel(1);
      }
    } else {
      // No zoom capability
      setZoomLevel(1);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }

    // Clear cached track references
    window.cameraTrack = null;
    window.cameraZoomCapabilities = null;
  }, [stream]);

  // Switch between front and rear cameras
  const switchCamera = useCallback(async () => {
    // Stop current stream cleanly
    stopCamera();

    // Clear cached track references (prevents errors when toggling cameras)
    window.cameraTrack = null;
    window.cameraZoomCapabilities = null;

    // Also clear the video element srcObject to prevent stale references
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    // Toggle facing mode
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    toast.info(`Switching to ${newFacing === 'environment' ? 'rear' : 'front'} camera...`);

    // Give the browser a moment to release the previous camera track
    await new Promise((r) => setTimeout(r, 150));

    // Try to start the new camera. If it fails, show a toast and stay on current facingMode.
    try {
      await startCamera(newFacing);
    } catch (e) {
      console.error('Switch camera error:', e);
      toast.error('Switch camera failed. Please try again.');
      // Attempt to restore previous camera if possible
      try {
        await startCamera(facingMode);
      } catch {
        // ignore
      }
    }
  }, [stopCamera, facingMode, startCamera]);

  // Toggle play/pause camera feed
  const toggleCameraPause = useCallback(() => {
    if (videoRef.current) {
      if (isCameraPaused) {
        videoRef.current.play();
        setIsCameraPaused(false);
        toast.info('▶️ Camera resumed');
      } else {
        videoRef.current.pause();
        setIsCameraPaused(true);
        toast.info('⏸️ Camera paused');
      }
    }
  }, [isCameraPaused]);

  useEffect(() => {
    console.log('🔄 Mode changed to:', mode, 'Stream exists:', !!stream);
    if (mode === "camera") {
      if (!stream) {
        console.log('📸 Initiating camera start...');
        startCamera();
      }
    } else {
      stopCamera();
    }
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Also try to start camera on initial mount
  useEffect(() => {
    console.log('🚀 Dashboard mounted, checking camera...');
    if (mode === "camera" && !stream) {
      console.log('📸 Initial camera start on mount...');
      startCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Track mode changes (used to avoid accidental auto-record)
  useEffect(() => {
    prevModeRef.current = mode;
  }, [mode]);

  // ULTRA CRYSTAL CLEAR Capture - Maximum quality frame capture
  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Use actual video resolution for ULTRA quality
    const actualWidth = video.videoWidth || 3840;
    const actualHeight = video.videoHeight || 2160;
    
    // Set canvas to full resolution
    canvas.width = actualWidth;
    canvas.height = actualHeight;
    
    const ctx = canvas.getContext("2d", { 
      alpha: false,
      desynchronized: true,
      willReadFrequently: false
    });
    
    // Enable highest quality rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Apply zoom with CRYSTAL CLEAR quality (no blur)
    if (zoomLevel > 1) {
      const zoomedWidth = actualWidth / zoomLevel;
      const zoomedHeight = actualHeight / zoomLevel;
      const offsetX = (actualWidth - zoomedWidth) / 2;
      const offsetY = (actualHeight - zoomedHeight) / 2;
      
      // Draw zoomed area at full resolution - CRYSTAL CLEAR
      ctx.drawImage(
        video,
        offsetX, offsetY, zoomedWidth, zoomedHeight,  // Source (zoomed region)
        0, 0, actualWidth, actualHeight               // Destination (full canvas)
      );
    } else {
      ctx.drawImage(video, 0, 0, actualWidth, actualHeight);
    }
    
    // Export at maximum JPEG quality (1.0 = 100%)
    const imageData = canvas.toDataURL("image/jpeg", 1.0);
    setCapturedImage(imageData);
    setEnhancedImage(null);
    setAnalysisResult(null);
    
    // Store for potential analysis and show prompt
    setPendingCaptureForAnalysis(imageData);
    setShowAnalyzePrompt(true);
    
    const megapixels = ((actualWidth * actualHeight) / 1000000).toFixed(1);
    toast.success(`📸 Captured! ${actualWidth}x${actualHeight} (${megapixels}MP)`);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImage(event.target.result);
      setCapturedImage(null);
      setEnhancedImage(null);
      setAnalysisResult(null);
      toast.success("Image uploaded!");
    };
    reader.readAsDataURL(file);
  };

  // Handle video upload
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("video/")) {
      toast.error("Please upload a video file");
      return;
    }

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video file too large. Maximum size is 100MB");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisProgress(10);
    toast.info("Uploading video...");

    try {
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await axios.post(`${API}/video/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (uploadResponse.data.success) {
        setVideoInfo(uploadResponse.data);
        setUploadedVideo(URL.createObjectURL(file));
        setAnalysisProgress(30);
        toast.success(`Video uploaded! Duration: ${uploadResponse.data.duration_seconds.toFixed(1)}s`);
        
        // Store for analysis
        setVideoInfo(uploadResponse.data);
      }
    } catch (error) {
      console.error("Video upload error:", error);
      toast.error("Failed to upload video");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Analyze uploaded video
  const analyzeVideo = async () => {
    if (!videoInfo) {
      toast.error("Please upload a video first");
      return;
    }

    // Check if premium (video analysis is premium only)
    if (!isPremium && !isOwner) {
      setShowQuickPayment(true);
      toast.error("Video analysis requires Premium!");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisProgress(40);
    toast.info("Analyzing video frames... This may take a minute.");

    try {
      const formData = new FormData();
      formData.append('video_id', videoInfo.video_id);
      formData.append('temp_path', videoInfo.temp_path);
      formData.append('filename', videoInfo.filename);
      formData.append('max_frames', '10'); // Analyze 10 key frames

      const response = await axios.post(`${API}/video/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setAnalysisProgress(100);
      setVideoAnalysisResult(response.data);
      toast.success("Video analysis complete!");
    } catch (error) {
      console.error("Video analysis error:", error);
      toast.error("Video analysis failed. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Apply filters without AI analysis
  const applyFilters = async () => {
    const imageToProcess = capturedImage || uploadedImage;
    if (!imageToProcess) {
      toast.error("Please capture or upload an image first");
      return;
    }

    if (activeFilters.length === 0) {
      toast.error("Please select at least one filter");
      return;
    }

    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API}/apply-filters`, {
        image_base64: imageToProcess,
        filters: activeFilters
      });

      setEnhancedImage(response.data.enhanced_image_base64);
      toast.success("Filters applied successfully!");
    } catch (error) {
      console.error("Filter error:", error);
      toast.error("Failed to apply filters");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // AI-POWERED Real-time filter enhancement
  const applyAIFilters = async () => {
    const imageToProcess = capturedImage || uploadedImage;
    if (!imageToProcess) {
      toast.error("Please capture or upload an image first");
      return;
    }

    setIsAnalyzing(true);
    try {
      // Use the AI-enhanced filter endpoint for TRUE AI processing
      const response = await axios.post(`${API}/filters/ai-enhance`, {
        image_base64: imageToProcess,
        filters: activeFilters,
        is_premium: isPremium || isOwner
      });

      if (response.data.success) {
        setEnhancedImage(response.data.enhanced_image_base64);
        
        // If anomalies were detected, show them
        if (response.data.anomalies_detected?.length > 0) {
          toast.success(`AI Enhanced! ${response.data.anomalies_detected.length} objects detected`);
        } else {
          toast.success(`AI Enhanced in ${response.data.processing_time_ms}ms`);
        }
        
        // Set partial analysis result if available
        if (response.data.ai_analysis) {
          setAnalysisResult({
            analysis: response.data.ai_analysis,
            anomalies_detected: response.data.anomalies_detected || [],
            confidence_score: response.data.confidence_score || 0
          });
        }
      }
    } catch (error) {
      console.error("AI Filter error:", error);
      toast.error("AI enhancement failed. Try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Full AI analysis
  const analyzeImage = async () => {
    const imageToProcess = capturedImage || uploadedImage;
    if (!imageToProcess) {
      toast.error("Please capture or upload an image first");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResult(null);

    try {
      const response = await axios.post(`${API}/analyze`, {
        image_base64: imageToProcess,
        filters: activeFilters,
        is_premium: isPremium || isOwner
      });

      setAnalysisResult(response.data);
      if (response.data.enhanced_image_base64) {
        setEnhancedImage(`data:image/jpeg;base64,${response.data.enhanced_image_base64}`);
      }
      toast.success("Analysis complete!");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Analysis failed. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // ===== Live AI (motion heatmap + GPT sampling) =====
  const computeMotionMask = (prevImageData, currImageData, width, height) => {
    // Quick grayscale absolute-diff mask (sample every 2px for speed)
    const step = 2;
    const mask = new Uint8ClampedArray(width * height);

    let motionPixels = 0;
    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        const idx = (y * width + x) * 4;
        const pr = prevImageData.data[idx];
        const pg = prevImageData.data[idx + 1];
        const pb = prevImageData.data[idx + 2];
        const cr = currImageData.data[idx];
        const cg = currImageData.data[idx + 1];
        const cb = currImageData.data[idx + 2];

        const p = (pr * 0.299 + pg * 0.587 + pb * 0.114);
        const c = (cr * 0.299 + cg * 0.587 + cb * 0.114);
        const d = Math.abs(c - p);

        if (d > 18) {
          mask[y * width + x] = 255;
          motionPixels++;
        }
      }
    }

    const motionRatio = motionPixels / ((width / step) * (height / step));
    return { mask, motionRatio };
  };

  const renderHeatmapOverlay = (mask, width, height) => {
    const heatCanvas = document.createElement('canvas');
    heatCanvas.width = width;
    heatCanvas.height = height;
    const hctx = heatCanvas.getContext('2d');
    const imgData = hctx.createImageData(width, height);

    for (let i = 0; i < width * height; i++) {
      const v = mask[i];
      const p = i * 4;
      if (v) {
        // red/yellow heat
        imgData.data[p] = 255;
        imgData.data[p + 1] = 80;
        imgData.data[p + 2] = 0;
        imgData.data[p + 3] = 140;
      } else {
        imgData.data[p + 3] = 0;
      }
    }

    hctx.putImageData(imgData, 0, 0);
    return heatCanvas.toDataURL('image/png', 0.6);
  };

  const captureSmallFrameDataUrl = () => {
    if (!videoRef.current || !canvasRef.current) return null;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const w = Math.min(video.videoWidth || 1280, 640);
    const h = Math.round(w * ((video.videoHeight || 720) / (video.videoWidth || 1280)));

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, w, h);

    return { dataUrl: canvas.toDataURL('image/jpeg', 0.7), ctx, w, h };
  };

  const runLiveAIStep = async () => {
    if (!isLiveAIOn || isAnalyzing) return;
    if (mode !== 'camera' || !videoRef.current) return;

    const cap = captureSmallFrameDataUrl();
    if (!cap) return;

    // motion gating
    const currImageData = cap.ctx.getImageData(0, 0, cap.w, cap.h);
    if (prevFrameRef.current) {
      const { mask, motionRatio } = computeMotionMask(prevFrameRef.current, currImageData, cap.w, cap.h);
      setMotionHeatmapDataUrl(renderHeatmapOverlay(mask, cap.w, cap.h));

      // If almost no motion, skip GPT to save cost
      if (motionRatio < 0.01) {
        prevFrameRef.current = currImageData;
        return;
      }
    }

    prevFrameRef.current = currImageData;

    try {
      setIsAnalyzing(true);
      const response = await axios.post(`${API}/analyze`, {
        image_base64: cap.dataUrl,
        filters: activeFilters,
        is_premium: isPremium || isOwner
      });

      setLastLiveAIResult(response.data);

      // Also keep the main analysis panel updated (so user sees results live)
      setAnalysisResult(response.data);
      if (response.data.enhanced_image_base64) {
        setEnhancedImage(`data:image/jpeg;base64,${response.data.enhanced_image_base64}`);
      }
    } catch (e) {
      console.log('Live AI step failed:', e);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleLiveAI = () => {
    const next = !isLiveAIOn;
    setIsLiveAIOn(next);

    if (!next) {
      if (liveAIIntervalRef.current) {
        clearInterval(liveAIIntervalRef.current);
        liveAIIntervalRef.current = null;
      }
      setMotionHeatmapDataUrl(null);
      prevFrameRef.current = null;
      toast.info('Live AI stopped');
      return;
    }

    // Start loop
    toast.success('Live AI started');
    prevFrameRef.current = null;
    if (liveAIIntervalRef.current) clearInterval(liveAIIntervalRef.current);
    liveAIIntervalRef.current = setInterval(() => {
      runLiveAIStep();
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (liveAIIntervalRef.current) {
        clearInterval(liveAIIntervalRef.current);
      }
    };
  }, []);

  // ===== AI ANOMALY TRACKING =====
  const runAnomalyTrackingStep = async () => {
    if (!isAnomalyTrackingOn || mode !== 'camera' || !videoRef.current) return;

    const cap = captureSmallFrameDataUrl();
    if (!cap) return;

    try {
      const response = await axios.post(`${API}/track-anomalies`, {
        image_base64: cap.dataUrl,
        previous_anomalies: previousAnomaliesRef.current,
        sensitivity: trackingSensitivity
      });

      const data = response.data;
      setTrackedAnomalies(data.anomalies || []);
      setAnomalyOverlayUrl(data.overlay_image_base64 ? `data:image/jpeg;base64,${data.overlay_image_base64}` : null);
      setFrameAnalysis(data.frame_analysis || "");
      
      // Store for next frame comparison
      previousAnomaliesRef.current = data.anomalies || [];

      // Alert if motion detected
      if (data.motion_detected) {
        toast.info("🚨 Motion detected!", { duration: 1500 });
      }
    } catch (e) {
      console.log('Anomaly tracking step failed:', e);
    }
  };

  const toggleAnomalyTracking = () => {
    const next = !isAnomalyTrackingOn;
    setIsAnomalyTrackingOn(next);

    if (!next) {
      if (anomalyTrackingRef.current) {
        clearInterval(anomalyTrackingRef.current);
        anomalyTrackingRef.current = null;
      }
      setAnomalyOverlayUrl(null);
      setTrackedAnomalies([]);
      setFrameAnalysis("");
      previousAnomaliesRef.current = [];
      toast.info('AI Tracking stopped');
      return;
    }

    toast.success('🎯 AI Anomaly Tracking started');
    previousAnomaliesRef.current = [];
    if (anomalyTrackingRef.current) clearInterval(anomalyTrackingRef.current);
    anomalyTrackingRef.current = setInterval(() => {
      runAnomalyTrackingStep();
    }, 500); // Fast tracking at 2fps
  };

  useEffect(() => {
    return () => {
      if (anomalyTrackingRef.current) {
        clearInterval(anomalyTrackingRef.current);
      }
    };
  }, []);

  // ===== AI ZOOM CLARITY =====
  const applyAIZoomClarity = useCallback(async () => {
    if (!aiClarityEnabled || zoomLevel < 5 || !videoRef.current || !canvasRef.current) return;
    
    const cap = captureSmallFrameDataUrl();
    if (!cap) return;

    setIsApplyingClarity(true);
    try {
      const response = await axios.post(`${API}/zoom-clarity`, {
        image_base64: cap.dataUrl,
        zoom_level: zoomLevel
      });

      if (response.data.clarity_applied) {
        setClarityDetails(response.data.enhancement_details);
      }
    } catch (e) {
      console.log('AI clarity failed:', e);
    } finally {
      setIsApplyingClarity(false);
    }
  }, [aiClarityEnabled, zoomLevel]);

  // Apply clarity when zoom changes significantly
  useEffect(() => {
    if (zoomLevel >= 5 && aiClarityEnabled) {
      const debounce = setTimeout(() => {
        applyAIZoomClarity();
      }, 300);
      return () => clearTimeout(debounce);
    }
  }, [zoomLevel, aiClarityEnabled, applyAIZoomClarity]);


  // Download enhanced image - works on mobile and desktop
  const downloadImage = async () => {
    const imageToDownload = enhancedImage || capturedImage || uploadedImage;
    if (!imageToDownload) return;

    const filename = `skybot-${Date.now()}.jpg`;
    
    try {
      // Try using Web Share API for mobile (allows saving to camera roll)
      if (navigator.share && navigator.canShare) {
        // Convert base64/dataURL to blob
        const response = await fetch(imageToDownload);
        const blob = await response.blob();
        const file = new File([blob], filename, { type: 'image/jpeg' });
        
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'Skybot Capture',
            text: 'Sky anomaly captured with Skybot'
          });
          toast.success("Image shared/saved!");
          return;
        }
      }
    } catch (shareError) {
      console.log("Share API not available, falling back to download:", shareError);
    }
    
    // Fallback to traditional download
    const link = document.createElement("a");
    link.href = imageToDownload;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Image downloaded!");
  };

  // Save video recording to device
  const saveVideoToDevice = async (blob, filename) => {
    try {
      // Try using Web Share API for mobile
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], filename, { type: blob.type });
        
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'Skybot Recording',
            text: 'Video recorded with Skybot'
          });
          toast.success("Video shared/saved!");
          return true;
        }
      }
    } catch (shareError) {
      console.log("Share API not available for video:", shareError);
    }
    
    // Fallback to download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Video downloaded!");
    return true;
  };

  // Submit Commercial Inquiry
  const submitPurchaseInquiry = async () => {
    if (!purchaseInquiry.name || !purchaseInquiry.email || !purchaseInquiry.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setIsSubmittingInquiry(true);
    try {
      const response = await axios.post(`${API}/commercial/inquiry`, purchaseInquiry);
      toast.success(response.data.message);
      setShowPurchaseModal(false);
      setPurchaseInquiry({
        name: '',
        email: '',
        company: '',
        inquiry_type: 'license',
        message: '',
        budget_range: ''
      });
    } catch (error) {
      console.error("Inquiry error:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmittingInquiry(false);
    }
  };

  // Fetch Celestial Data
  const fetchCelestialData = async () => {
    try {
      // Get user's location if available
      let lat = 0, lon = 0;
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            lat = position.coords.latitude;
            lon = position.coords.longitude;
          },
          () => {} // Ignore errors, use default
        );
      }
      
      const response = await axios.get(`${API}/celestial/current?lat=${lat}&lon=${lon}`);
      setCelestialData(response.data);
    } catch (error) {
      console.error("Celestial data error:", error);
    }
  };

  // Toggle Celestial Overlay
  const toggleCelestialOverlay = () => {
    if (!showCelestialOverlay) {
      fetchCelestialData();
    }
    setShowCelestialOverlay(!showCelestialOverlay);
  };

  // Real-time Analysis (for premium users)
  const startRealTimeAnalysis = async () => {
    if (!isPremium && !isOwner) {
      toast.error("Real-time analysis requires Premium!");
      setShowQuickPayment(true);
      return;
    }
    
    setIsRealTimeMode(true);
    toast.success("Real-time AI analysis activated");
    
    // Analyze every 5 seconds
    const interval = setInterval(async () => {
      if (videoRef.current && canvasRef.current) {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const imageData = canvas.toDataURL('image/jpeg', 0.7);
        
        try {
          const response = await axios.post(`${API}/analyze/realtime`, {
            image_base64: imageData
          });
          setRealTimeAnalysis(response.data);
        } catch (error) {
          console.error("Real-time analysis error:", error);
        }
      }
    }, 5000);
    
    setRealTimeInterval(interval);
  };

  const stopRealTimeAnalysis = () => {
    if (realTimeInterval) {
      clearInterval(realTimeInterval);
      setRealTimeInterval(null);
    }
    setIsRealTimeMode(false);
    setRealTimeAnalysis(null);
    toast.info("Real-time analysis stopped");
  };

  // Reset everything
  const resetAnalysis = () => {
    setCapturedImage(null);
    setUploadedImage(null);
    setEnhancedImage(null);
    setAnalysisResult(null);
    setZoomLevel(1);
    setUploadedVideo(null);
    setVideoInfo(null);
    setVideoAnalysisResult(null);
    setAnalysisProgress(0);
    stopRecording();
    stopRealTimeAnalysis();
    setFilters({
      sun_dimming: false,
      night_mode: false,
      noise_reduction: false,
      star_enhancement: false,
      anomaly_highlight: false,
      "4k_upscale": false,
      night_vision: false,
      infrared: false,
      thermal: false,
      motion_enhance: false,
      auto_clarity: false,
      object_tracking: false
    });
  };

  const toggleFilter = (filterName) => {
    // Check if premium filter and user doesn't have access
    const premiumFilters = [
      'star_enhancement', 'deep_sky', 'anomaly_highlight', 
      '4k_upscale', '8k_ultra', 'night_vision', 'night_vision_white', 
      'starlight', 'infrared', 'thermal', 'thermal_ironbow', 'thermal_rainbow',
      'motion_enhance', 'auto_clarity', 'object_tracking', 'ufo_track',
      'meteor_track', 'planet_enhance', 'hdr_enhance'
    ];
    if (premiumFilters.includes(filterName) && !isPremium && !isOwner) {
      setShowQuickPayment(true);
      return;
    }
    
    setFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  // Zoom controls
  const handleZoomChange = (newZoom) => {
    if (!isPremium && !isOwner && newZoom > 10) {
      setShowQuickPayment(true);
      toast.error("Ultra Zoom (10x+) requires Premium!");
      return;
    }
    setZoomLevel(Math.min(Math.max(newZoom, 1), MAX_ZOOM));
  };

  const zoomIn = () => handleZoomChange(zoomLevel * 1.5);
  const zoomOut = () => handleZoomChange(zoomLevel / 1.5);

  // Recording functions (Premium feature)
  const startRecording = async () => {
    if (!isPremium && !isOwner) {
      setShowQuickPayment(true);
      toast.error("Ultra HD Recording requires Premium!");
      return;
    }

    if (!stream) {
      toast.error("Camera not available. Please allow camera access first.");
      return;
    }

    try {
      // Determine best supported MIME type for cross-browser compatibility
      let mimeType = 'video/webm';
      let fileExtension = 'webm';
      
      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
        mimeType = 'video/webm;codecs=vp9';
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
        mimeType = 'video/webm;codecs=vp8';
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        mimeType = 'video/webm';
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        mimeType = 'video/mp4';
        fileExtension = 'mp4';
      }
      
      console.log('Using MIME type for recording:', mimeType);
      
      const options = {
        mimeType: mimeType,
        videoBitsPerSecond: 8000000 // 8 Mbps for high quality
      };
      
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunks, { type: mimeType });
        const filename = `skybot-recording-${Date.now()}.${fileExtension}`;
        
        // Use improved save function that supports mobile camera roll
        await saveVideoToDevice(blob, filename);
        
        // Also set it for video analysis
        const url = URL.createObjectURL(blob);
        setUploadedVideo(url);
        setVideoInfo({
          video_id: `recording-${Date.now()}`,
          filename: filename,
          duration_seconds: recordingDuration,
          frame_count: Math.floor(recordingDuration * 30),
          fps: 30
        });
        
        // Show analyze prompt for video
        setPendingCaptureForAnalysis(url);
        setShowAnalyzePrompt(true);
        
        setRecordedChunks([]);
        toast.success(`🎬 Video saved! ${formatTime(recordingDuration)}`);
      };
      
      mediaRecorder.onerror = (e) => {
        console.error("MediaRecorder error:", e);
        toast.error("Recording error occurred");
        setIsRecording(false);
      };
      
      mediaRecorder.start(1000); // Capture in 1-second chunks
      setIsRecording(true);
      setRecordingDuration(0);
      
      // Start duration counter
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
      toast.success("Recording started - Ultra HD quality");
    } catch (error) {
      console.error("Recording error:", error);
      toast.error("Failed to start recording. " + (error.message || "Please check camera permissions."));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  // Format recording time
  const formatTime = (seconds) => {
    if (seconds == null || Number.isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Timelapse Functions
  const startTimelapse = async () => {
    if (mode !== 'camera') {
      toast.error('Timelapse requires Live Camera mode');
      return;
    }
    if (!isPremium && !isOwner) {
      setShowQuickPayment(true);
      toast.error("Timelapse requires Premium!");
      return;
    }

    try {
      const response = await axios.post(`${API}/timelapse/start`);
      if (response.data.success) {
        setTimelapseSessionId(response.data.session_id);
        setIsTimelapseMode(true);
        setTimelapseFrameCount(0);
        toast.success("Timelapse started! Capturing frames...");
        
        // Auto-capture frames every 2 seconds
        const interval = setInterval(async () => {
          if (videoRef.current && canvasRef.current) {
            const canvas = canvasRef.current;
            const video = videoRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            
            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0);
            
            const imageData = canvas.toDataURL("image/jpeg", 0.8);
            
            try {
              const blob = await (await fetch(imageData)).blob();
              const formData = new FormData();
              formData.append('image', blob, `frame-${Date.now()}.jpg`);
              formData.append('apply_clarity', 'true');
              
              const res = await axios.post(
                `${API}/timelapse/add-frame/${response.data.session_id}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
              );
              
              setTimelapseFrameCount(res.data.total_frames);
            } catch (e) {
              console.error("Frame capture error:", e);
            }
          }
        }, 2000);
        
        setTimelapseInterval(interval);
      }
    } catch (error) {
      console.error("Timelapse start error:", error);
      toast.error("Failed to start timelapse");
    }
  };

  const stopTimelapse = async () => {
    if (timelapseInterval) {
      clearInterval(timelapseInterval);
      setTimelapseInterval(null);
    }
    
    if (!timelapseSessionId) return;
    
    if (timelapseFrameCount < 2) {
      toast.error("Need at least 2 frames for timelapse");
      setIsTimelapseMode(false);
      return;
    }

    toast.info("Generating timelapse video...");
    
    try {
      const formData = new FormData();
      formData.append('fps', '10');
      
      const response = await axios.post(
        `${API}/timelapse/generate/${timelapseSessionId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        setTimelapseVideo(response.data.video_base64);
        toast.success(`Timelapse created! ${response.data.frame_count} frames, ${response.data.duration_seconds.toFixed(1)}s`);
      }
    } catch (error) {
      console.error("Timelapse generation error:", error);
      toast.error("Failed to generate timelapse");
    } finally {
      setIsTimelapseMode(false);
      setTimelapseSessionId(null);
    }
  };

  // AI Video Ad Generation
  const generateAdVideo = async () => {
    if (!isPremium && !isOwner) {
      setShowQuickPayment(true);
      toast.error("AI Video Generation requires Premium!");
      return;
    }

    if (!adPrompt.trim()) {
      toast.error("Please enter a prompt for your video");
      return;
    }

    setIsGeneratingAd(true);
    toast.info("Generating AI video ad... This may take 2-5 minutes.");

    try {
      const response = await axios.post(`${API}/generate-ad-video`, {
        prompt: adPrompt,
        duration: adDuration,
        size: adSize,
        model: "sora-2"
      });

      if (response.data.success) {
        setGeneratedAdVideo(response.data.video_base64);
        toast.success("AI video ad generated successfully!");
      }
    } catch (error) {
      console.error("Video generation error:", error);
      toast.error("Failed to generate video. Please try again.");
    } finally {
      setIsGeneratingAd(false);
    }
  };

  const downloadGeneratedVideo = () => {
    if (!generatedAdVideo) return;
    
    const link = document.createElement("a");
    link.href = generatedAdVideo;
    link.download = `aether-lens-ad-${Date.now()}.mp4`;
    link.click();
    toast.success("Video downloaded!");
  };

  const currentImage = enhancedImage || capturedImage || uploadedImage;
  const isLiveCameraView = mode === "camera" && !currentImage;


  return (
    <div 
      className="min-h-screen flex flex-col lg:flex-row relative" 
      style={{ 
        background: `linear-gradient(135deg, ${theme.colors.void} 0%, ${theme.colors.background} 50%, ${theme.colors.void} 100%)`
      }}
    >
      {/* Cohesive 8K Sky backdrop (mirrors the Landing Page) */}
      <SkyThemeBackground
        currentTheme={currentSkyTheme}
        activeTheme={activeSkyTheme}
        dim={0.25}
      />
      
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Mobile Header - Theme colored */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3 backdrop-blur-xl" 
        style={{ 
          background: `linear-gradient(180deg, ${theme.colors.void}f0 0%, ${theme.colors.background}e0 100%)`, 
          borderBottom: `1px solid ${theme.colors.primary}30`,
          boxShadow: `0 4px 20px ${theme.colors.primary}10`
        }}
      >
        <div className="flex items-center gap-3">
          <button
            data-testid="mobile-home-btn"
            onClick={() => navigate("/")}
            className="p-2 rounded-xl transition-colors"
            style={{ background: `${theme.colors.primary}15`, color: theme.colors.primary }}
          >
            <Home className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Aperture className="w-6 h-6" style={{ color: theme.colors.primary }} />
            <span className="font-unbounded font-bold text-white">Skybot</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Guided Tour Trigger */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={startTour}
            className="p-2 rounded-xl transition-colors"
            style={{
              background: 'rgba(0,0,0,0.5)',
              border: `1px solid ${theme.colors.primary}30`,
              color: theme.colors.primary
            }}
            title="Guided tour"
            data-testid="tour-btn"
          >
            <span className="font-mono text-sm font-bold">?</span>
          </motion.button>

          {(currentImage || uploadedVideo) && (
            <button
              data-testid="mobile-reset-btn"
              onClick={resetAnalysis}
              className="p-2 rounded-xl transition-colors"
              style={{ background: 'rgba(255,255,255,0.1)', color: theme.colors.textMuted }}
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          )}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-xl transition-colors"
            style={{ background: 'rgba(255,255,255,0.1)', color: theme.colors.textMuted }}
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden fixed top-16 left-0 right-0 z-40 p-4 backdrop-blur-xl"
            style={{ 
              background: 'rgba(2,4,10,0.95)', 
              borderBottom: `1px solid ${theme.colors.border}` 
            }}
          >
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setMode("camera"); resetAnalysis(); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 p-3 rounded-xl transition-colors"
                style={{
                  background: mode === "camera" ? `${theme.colors.primary}20` : 'rgba(255,255,255,0.05)',
                  color: mode === "camera" ? theme.colors.primary : theme.colors.textMuted
                }}
              >
                <Camera className="w-5 h-5" />
                <span className="font-exo2 text-sm">{t('liveCamera')}</span>
              </button>
              <button
                onClick={() => { setMode("upload"); stopCamera(); resetAnalysis(); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 p-3 rounded-xl transition-colors"
                style={{
                  background: mode === "upload" ? `${theme.colors.primary}20` : 'rgba(255,255,255,0.05)',
                  color: mode === "upload" ? theme.colors.primary : theme.colors.textMuted
                }}
              >
                <Upload className="w-5 h-5" />
                <span className="font-exo2 text-sm">{t('upload')}</span>
              </button>
              <button
                onClick={() => { setMode("video"); stopCamera(); resetAnalysis(); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 p-3 rounded-xl transition-colors"
                style={{
                  background: mode === "video" ? `${theme.colors.accent}20` : 'rgba(255,255,255,0.05)',
                  color: mode === "video" ? theme.colors.accent : theme.colors.textMuted
                }}
              >
                <Film className="w-5 h-5" />
                <span className="font-exo2 text-sm">{t('video')}</span>
              </button>
              <button
                onClick={() => { navigate("/gallery"); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 p-3 rounded-xl transition-colors"
                style={{ background: 'rgba(255,255,255,0.05)', color: theme.colors.textMuted }}
              >
                <History className="w-5 h-5" />
                <span className="font-exo2 text-sm">{t('gallery')}</span>
              </button>
              {/* Star Map Button */}
              <button
                onClick={() => { setShowStarMap(true); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 p-3 rounded-xl transition-colors"
                style={{ background: `${theme.colors.primary}15`, color: theme.colors.primary }}
                data-testid="mobile-star-map-btn"
              >
                <Map className="w-5 h-5" />
                <span className="font-exo2 text-sm">Star Map</span>
              </button>
              {/* Night Sky Alerts */}
              <button
                onClick={() => { setShowNightSkyAlerts(true); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 p-3 rounded-xl transition-colors"
                style={{ background: 'rgba(255,215,0,0.1)', color: '#ffd700' }}
                data-testid="mobile-alerts-btn"
              >
                <Bell className="w-5 h-5" />
                <span className="font-exo2 text-sm">Sky Alerts</span>
              </button>
            </div>
            
            {/* Fullscreen Button */}
            {mode === "camera" && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <button
                  onClick={() => { setIsFullscreen(true); setMobileMenuOpen(false); }}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-xl transition-colors"
                  style={{ background: `${theme.colors.accent}15`, color: theme.colors.accent }}
                  data-testid="fullscreen-btn"
                >
                  <Maximize className="w-5 h-5" />
                  <span className="font-exo2 text-sm">{t('fullscreen')}</span>
                </button>
              </div>
            )}
            
            {/* Language & Theme Options */}
            <div className="mt-3 pt-3 border-t border-white/10 flex gap-2">
              <button
                onClick={() => { setShowOwnerModal(true); setMobileMenuOpen(false); }}
                className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl transition-colors"
                style={{ background: 'rgba(255,255,255,0.05)', color: theme.colors.textMuted }}
                data-testid="mobile-owner-btn"
              >
                {isOwner ? <Unlock className="w-5 h-5 text-[#ffd700]" /> : <Lock className="w-5 h-5" />}
                <span className="font-exo2 text-sm">{isOwner ? 'Owner Mode' : 'Owner Login'}</span>
              </button>
              {isOwner && (
                <button
                  onClick={() => setShowThemeSelector(!showThemeSelector)}
                  className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl transition-colors"
                  style={{ background: `${theme.colors.primary}10`, color: theme.colors.primary }}
                  data-testid="mobile-theme-btn"
                >
                  <Palette className="w-5 h-5" />
                  <span className="font-exo2 text-sm">{t('themes')}</span>
                </button>
              )}
              <div className="flex-1">
                <LanguageSelector />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guided Tour - show on all views */}
      <GuidedTour isOpen={showTour} onClose={endTour} />

      {/* Fullscreen Camera View */}
      <FullscreenCamera
        isOpen={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        videoRef={videoRef}
        stream={stream}
        filters={filters}
        toggleFilter={toggleFilter}
        setFilters={setFilters}
        isPremium={isPremium}
        isOwner={isOwner}
        onCapture={captureFrame}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
        isRecording={isRecording}
        recordingDuration={recordingDuration}
        zoomLevel={zoomLevel}
        setZoomLevel={setZoomLevel}
        applyZoom={applyZoom}
        MAX_ZOOM={MAX_ZOOM}
        onSwitchCamera={switchCamera}
        facingMode={facingMode}
        capturedImage={capturedImage}
        analysisResult={analysisResult}
        isAnalyzing={isAnalyzing}
        onAnalyze={analyzeImage}
      />

      {/* Sidebar - Hidden on Mobile */}
      <aside className="hidden lg:flex w-20 lg:w-64 flex-col relative z-10" style={{ background: 'rgba(8,12,24,0.55)', backdropFilter: 'blur(18px) saturate(140%)', WebkitBackdropFilter: 'blur(18px) saturate(140%)', borderRight: `1px solid ${theme.colors.border}` }}>
        <div className="p-4 lg:p-6" style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
          <div className="flex items-center gap-3">
            <Aperture className="w-8 h-8" style={{ color: theme.colors.primary }} />
            <span className="hidden lg:block font-unbounded font-bold" style={{ color: theme.colors.text }}>Skybot</span>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            <li>
              <button
                data-testid="nav-camera-btn"
                onClick={() => { setMode("camera"); resetAnalysis(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors"
                style={{
                  background: mode === "camera" ? `${theme.colors.primary}15` : 'transparent',
                  color: mode === "camera" ? theme.colors.primary : theme.colors.textMuted
                }}
              >
                <Camera className="w-5 h-5" />
                <span className="hidden lg:block font-exo2">Live Camera</span>
              </button>
            </li>
            <li>
              <button
                data-testid="nav-upload-btn"
                onClick={() => { setMode("upload"); stopCamera(); resetAnalysis(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors"
                style={{
                  background: mode === "upload" ? `${theme.colors.primary}15` : 'transparent',
                  color: mode === "upload" ? theme.colors.primary : theme.colors.textMuted
                }}
              >
                <Upload className="w-5 h-5" />
                <span className="hidden lg:block font-exo2">Upload Image</span>
              </button>
            </li>
            <li>
              <button
                data-testid="nav-video-btn"
                onClick={() => { setMode("video"); stopCamera(); resetAnalysis(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors"
                style={{
                  background: mode === "video" ? `${theme.colors.accent}15` : 'transparent',
                  color: mode === "video" ? theme.colors.accent : theme.colors.textMuted
                }}
              >
                <Film className="w-5 h-5" />
                <span className="hidden lg:block font-exo2">Video Analysis</span>
                {!isPremium && !isOwner && <Crown className="w-3 h-3 ml-auto" style={{ color: theme.colors.accent }} />}
              </button>
            </li>
            <li>
              <button
                data-testid="nav-gallery-btn"
                onClick={() => navigate("/gallery")}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors hover:bg-white/5"
                style={{ color: theme.colors.textMuted }}
              >
                <History className="w-5 h-5" />
                <span className="hidden lg:block font-exo2">Gallery</span>
              </button>
            </li>
            
            {/* Star Map - Constellation Finder */}
            <li>
              <button
                data-testid="star-map-btn"
                onClick={() => setShowStarMap(true)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors hover:bg-white/5"
                style={{ 
                  color: theme.colors.primary,
                  background: `${theme.colors.primary}10`
                }}
              >
                <Map className="w-5 h-5" style={{ color: theme.colors.primary }} />
                <span className="hidden lg:block font-exo2 font-medium">Star Map</span>
                <Star className="w-3 h-3 ml-auto" style={{ color: '#ffd700' }} />
              </button>
            </li>

            {/* Night Sky Alerts */}
            <li>
              <button
                data-testid="night-alerts-btn"
                onClick={() => setShowNightSkyAlerts(true)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors hover:bg-white/5"
                style={{ 
                  color: '#ffd700',
                  background: 'rgba(255,215,0,0.1)'
                }}
              >
                <Bell className="w-5 h-5" style={{ color: '#ffd700' }} />
                <span className="hidden lg:block font-exo2 font-medium">Sky Alerts</span>
                <Sparkles className="w-3 h-3 ml-auto" style={{ color: '#ff6b4a' }} />
              </button>
            </li>
            
            <li>
              <button
                data-testid="share-btn"
                onClick={() => setShowShareModal(true)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors hover:bg-white/5"
                style={{ color: theme.colors.textMuted }}
              >
                <Share2 className="w-5 h-5" />
                <span className="hidden lg:block font-exo2">Share App</span>
              </button>
            </li>
            <li>
              <button
                data-testid="ai-ad-btn"
                onClick={() => setShowAdGeneratorModal(true)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors hover:bg-white/5"
                style={{ color: theme.colors.textMuted }}
              >
                <Film className="w-5 h-5 text-[#a855f7]" />
                <span className="hidden lg:block font-exo2">AI Video Ad</span>
                {!isPremium && !isOwner && <Crown className="w-3 h-3 text-[#ffd700] ml-auto" />}
              </button>
            </li>
            
            {/* Quick Upgrade to Premium - Direct Cash App */}
            {!isPremium && !isOwner && (
              <li>
                <button
                  data-testid="quick-upgrade-btn"
                  onClick={() => setShowQuickPayment(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all hover:scale-[1.02]"
                  style={{ 
                    background: 'linear-gradient(135deg, #00D632 0%, #00B82B 100%)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(0,214,50,0.3)'
                  }}
                >
                  <DollarSign className="w-5 h-5" />
                  <span className="hidden lg:block font-exo2 font-bold">Get Premium</span>
                  <Crown className="w-4 h-4 ml-auto text-[#ffd700]" />
                </button>
              </li>
            )}
            
            {/* Purchase / Commercial Inquiry */}
            <li>
              <button
                data-testid="purchase-btn"
                onClick={() => setShowPurchaseModal(true)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(34,197,94,0.1) 0%, rgba(16,185,129,0.1) 100%)',
                  color: '#22c55e',
                  border: '1px solid rgba(34,197,94,0.3)'
                }}
              >
                <ShoppingCart className="w-5 h-5" />
                <span className="hidden lg:block font-exo2 font-medium">Purchase App</span>
              </button>
            </li>
            
            {/* Download/Install App */}
            <li>
              <DownloadAppButton variant="sidebar" />
            </li>
          </ul>
        </nav>

        <div className="p-4 border-t border-white/5 space-y-2">
          {/* Theme Selector Button - Owner Only */}
          {isOwner && (
            <button
              data-testid="theme-btn"
              onClick={() => setShowThemeSelector(true)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-slate-400 hover:bg-white/5 transition-colors"
              style={{ color: theme.colors.primary }}
            >
              <Palette className="w-5 h-5" />
              <span className="hidden lg:block font-exo2 text-xs">Themes</span>
            </button>
          )}


          <button
            data-testid="back-home-btn"
            onClick={() => navigate("/")}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-slate-400 hover:bg-white/5 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="hidden lg:block font-exo2">Back</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col lg:flex-row pt-16 lg:pt-0 relative z-10">
        {/* Viewport */}
        <div className="flex-1 p-4 lg:p-6">
          <div className="h-full flex flex-col">
            {/* Mode Header with Back/Home Buttons */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {/* Back to Camera / Home Button for Desktop */}
                <div className="hidden lg:flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    data-testid="desktop-home-btn"
                    onClick={() => navigate("/")}
                    className="p-2 rounded-xl transition-colors"
                    style={{ 
                      background: `${theme.colors.primary}15`, 
                      color: theme.colors.primary,
                      border: `1px solid ${theme.colors.primary}30`
                    }}
                  >
                    <Home className="w-5 h-5" />
                  </motion.button>
                </div>
                
                <h1 className="font-unbounded font-bold text-xl text-white flex items-center gap-3">
                  {mode === "camera" ? (
                    <>
                      <Camera className="w-6 h-6 text-[#00f2ea]" />
                      Live Camera
                    </>
                  ) : mode === "video" ? (
                    <>
                      <Film className="w-6 h-6 text-[#ffd700]" />
                      Video Analysis
                      <span className="px-2 py-1 bg-[#ffd700]/20 text-[#ffd700] rounded text-xs font-mono">
                        PREMIUM
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-[#00f2ea]" />
                      Upload Image
                    </>
                  )}
                  {isOwner && (
                    <span className="ml-2 px-2 py-1 bg-[#ffd700]/20 text-[#ffd700] rounded text-xs font-mono">
                      OWNER
                    </span>
                  )}
                </h1>
              </div>
              
              {/* Contextual Action Buttons */}
              <div className="flex gap-2">
                {/* Back to Camera (when image is captured) */}
                {currentImage && mode === "camera" && (
                  <motion.button
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="back-to-camera-btn"
                    onClick={() => {
                      setCapturedImage(null);
                      setEnhancedImage(null);
                      setAnalysisResult(null);
                    }}
                    className="hidden sm:flex text-sm py-2 px-4 items-center gap-2 rounded-xl transition-colors"
                    style={{ 
                      background: `${theme.colors.primary}15`,
                      color: theme.colors.primary,
                      border: `1px solid ${theme.colors.primary}30`
                    }}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Retake
                  </motion.button>
                )}
                
                {/* Back to Upload (when image is uploaded) */}
                {uploadedImage && mode === "upload" && (
                  <motion.button
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="back-to-upload-btn"
                    onClick={() => {
                      setUploadedImage(null);
                      setEnhancedImage(null);
                      setAnalysisResult(null);
                    }}
                    className="hidden sm:flex text-sm py-2 px-4 items-center gap-2 rounded-xl transition-colors"
                    style={{ 
                      background: `${theme.colors.primary}15`,
                      color: theme.colors.primary,
                      border: `1px solid ${theme.colors.primary}30`
                    }}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    New Upload
                  </motion.button>
                )}
                
                {(currentImage || uploadedVideo) && (
                  <>
                    <button
                      data-testid="share-result-btn"
                      onClick={() => setShowShareModal(true)}
                      className="btn-secondary text-sm py-2 px-4 flex items-center gap-2"
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Share</span>
                    </button>
                    <button
                      data-testid="reset-btn"
                      onClick={resetAnalysis}
                      className="btn-secondary text-sm py-2 px-4 flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span className="hidden sm:inline">New Scan</span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Video Mode UI */}
            {mode === "video" && (
              <div className="flex-1 flex flex-col">
                {/* Video Upload Area */}
                {!uploadedVideo && (
                  <div 
                    className="flex-1 camera-container min-h-[400px] rounded-xl overflow-hidden flex flex-col items-center justify-center cursor-pointer hover:bg-white/5 transition-colors border-2 border-dashed border-[#ffd700]/30"
                    onClick={() => videoInputRef.current?.click()}
                  >
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                      data-testid="video-input"
                    />
                    <Film className="w-20 h-20 text-[#ffd700]/50 mb-4" />
                    <p className="font-unbounded font-bold text-xl text-white mb-2">Upload Sky Video</p>
                    <p className="font-exo2 text-slate-400 text-center max-w-md">
                      Upload a video of the sky for AI-powered frame-by-frame anomaly detection
                    </p>
                    <p className="font-mono text-xs text-slate-500 mt-4">
                      MP4, MOV, AVI, WEBM • Max 100MB
                    </p>
                  </div>
                )}

                {/* Video Preview */}
                {uploadedVideo && (
                  <div className="flex-1 camera-container min-h-[400px] rounded-xl overflow-hidden relative">
                    <video
                      src={uploadedVideo}
                      controls
                      className="w-full h-full object-contain"
                      data-testid="video-preview"
                    />
                    
                    {/* Video Info Overlay */}
                    {videoInfo && (
                      <div className="absolute top-20 lg:top-4 left-4 glass-panel px-4 py-2 z-10">
                        <div className="flex items-center gap-4 font-mono text-xs text-[#00f2ea]">
                          <span>{videoInfo.duration_seconds.toFixed(1)}s</span>
                          <span>{videoInfo.frame_count} frames</span>
                          <span>{videoInfo.fps.toFixed(0)} FPS</span>
                        </div>
                      </div>
                    )}

                    {/* Analysis Progress */}
                    {isAnalyzing && (
                      <div className="absolute inset-0 bg-[#02040a]/80 flex flex-col items-center justify-center z-20">
                        <Loader2 className="w-12 h-12 text-[#ffd700] animate-spin mb-4" />
                        <p className="font-exo2 text-white mb-2">Analyzing video frames...</p>
                        <div className="w-64 h-2 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#ffd700] transition-all duration-300"
                            style={{ width: `${analysisProgress}%` }}
                          />
                        </div>
                        <p className="font-mono text-xs text-slate-500 mt-2">{analysisProgress}% complete</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Video Action Buttons */}
                <div className="flex flex-wrap gap-3 mt-4">
                  {!uploadedVideo && (
                    <button
                      onClick={() => videoInputRef.current?.click()}
                      className="btn-primary flex-1 sm:flex-none"
                      style={{ background: '#ffd700', color: '#02040a' }}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <Film className="w-5 h-5" />
                        Select Video
                      </span>
                    </button>
                  )}

                  {uploadedVideo && !videoAnalysisResult && (
                    <button
                      onClick={analyzeVideo}
                      disabled={isAnalyzing}
                      className="btn-primary flex-1 sm:flex-none disabled:opacity-50"
                      style={{ background: '#ffd700', color: '#02040a' }}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <Scan className="w-5 h-5" />
                        Analyze Video
                      </span>
                    </button>
                  )}
                </div>

                {/* Video Analysis Results */}
                {videoAnalysisResult && (
                  <div className="mt-6 analysis-panel">
                    <h3 className="font-unbounded font-semibold text-lg text-[#ffd700] mb-4 flex items-center gap-2">
                      <Film className="w-5 h-5" />
                      Video Analysis Results
                    </h3>

                    {/* Summary */}
                    <div className="mb-6 p-4 bg-[#02040a] rounded-2xl">
                      <p className="font-exo2 text-slate-300">{videoAnalysisResult.summary}</p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="text-center p-3 bg-[#02040a] rounded-2xl">
                        <div className="font-unbounded font-bold text-2xl text-[#00f2ea]">
                          {videoAnalysisResult.frames_analyzed}
                        </div>
                        <div className="font-mono text-xs text-slate-500">Frames Analyzed</div>
                      </div>
                      <div className="text-center p-3 bg-[#02040a] rounded-2xl">
                        <div className="font-unbounded font-bold text-2xl text-[#ffd700]">
                          {videoAnalysisResult.total_anomalies?.length || 0}
                        </div>
                        <div className="font-mono text-xs text-slate-500">Anomalies Found</div>
                      </div>
                      <div className="text-center p-3 bg-[#02040a] rounded-2xl">
                        <div className="font-unbounded font-bold text-2xl text-[#00f2ea]">
                          {videoAnalysisResult.overall_confidence?.toFixed(0)}%
                        </div>
                        <div className="font-mono text-xs text-slate-500">Confidence</div>
                      </div>
                    </div>

                    {/* Anomalies */}
                    {videoAnalysisResult.total_anomalies?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-exo2 font-medium text-[#ff2e2e] mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Detected Anomalies
                        </h4>
                        <div className="space-y-2">
                          {videoAnalysisResult.total_anomalies.map((anomaly, i) => (
                            <div key={i} className="anomaly-item">
                              <p className="font-exo2 text-sm text-white">{anomaly}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Frame-by-Frame Details */}
                    <details className="group">
                      <summary className="cursor-pointer font-exo2 font-medium text-slate-300 hover:text-white">
                        View Frame-by-Frame Analysis →
                      </summary>
                      <div className="mt-4 space-y-4 max-h-64 overflow-y-auto">
                        {videoAnalysisResult.frame_analyses?.map((frame, i) => (
                          <div key={i} className="p-3 bg-[#02040a] rounded-2xl">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-mono text-xs text-[#00f2ea]">
                                Frame #{frame.frame_number} • {frame.timestamp?.toFixed(1)}s
                              </span>
                              <span className="font-mono text-xs text-slate-500">
                                {frame.confidence?.toFixed(0)}% confidence
                              </span>
                            </div>
                            <p className="font-mono text-xs text-slate-400 whitespace-pre-wrap">
                              {frame.analysis}
                            </p>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            )}

            {/* Camera/Image View (existing) */}
            {mode !== "video" && (
            <>
            <div 
              ref={cameraContainerRef}
              data-testid="camera-view"
              className="relative flex-1 min-h-[400px] rounded-2xl overflow-hidden touch-none" 
              style={{ 
                background: 'linear-gradient(135deg, rgba(2,4,10,0.35) 0%, rgba(10,15,30,0.35) 100%)',
                boxShadow: `0 0 60px ${theme.colors.primary}15, inset 0 0 80px rgba(0,0,0,0.45)`,
                border: `1px solid ${theme.colors.primary}25`
              }}
            >
              {/* Gesture Controls Overlay */}
              <GestureControls
                containerRef={cameraContainerRef}
                enabled={gesturesEnabled && !showTour && !isFullscreen && mode === 'camera' && !currentImage}
                onZoom={applyZoom}
                zoomLevel={zoomLevel}
                maxZoom={MAX_ZOOM}
                minZoom={0.5}
                onCapture={captureFrame}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
                isRecording={isRecording}
                onNextFilter={() => {
                  const filterKeys = Object.keys(filters);
                  const activeIndex = filterKeys.findIndex(k => filters[k]);
                  const nextIndex = (activeIndex + 1) % filterKeys.length;
                  toggleFilter(filterKeys[nextIndex]);
                }}
                onPrevFilter={() => {
                  const filterKeys = Object.keys(filters);
                  const activeIndex = filterKeys.findIndex(k => filters[k]);
                  const prevIndex = activeIndex <= 0 ? filterKeys.length - 1 : activeIndex - 1;
                  toggleFilter(filterKeys[prevIndex]);
                }}
                onFullscreen={() => setIsFullscreen(true)}
                onReset={resetAnalysis}
                onSwitchCamera={switchCamera}
                onAnalyze={analyzeImage}
                audioEnabled={true}
              />

              {/* AR Constellation Overlay */}
              {mode === 'camera' && !currentImage && (
                <>
                  <ARConstellationOverlay
                    enabled={arEnabled}
                    showLabels={arShowLabels}
                    showLines={arShowLines}
                    showPlanets={arShowPlanets}
                    opacity={0.85}
                  />
                  <ARControls
                    arEnabled={arEnabled}
                    setArEnabled={setArEnabled}
                    showLabels={arShowLabels}
                    setShowLabels={setArShowLabels}
                    showLines={arShowLines}
                    setShowLines={setArShowLines}
                    showPlanets={arShowPlanets}
                    setShowPlanets={setArShowPlanets}
                  />
                </>
              )}

              {/* Futuristic HUD Viewfinder Overlay */}
              <ViewfinderOverlay 
                isRecording={isRecording} 
                isScanning={isAnalyzing}
                isLocked={!!currentImage}
              />

              {/* Zoom UI removed (per request) */}

              <div className="absolute right-4 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-2">
                {/* Fullscreen Button */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsFullscreen(true)}
                  className="w-11 h-11 backdrop-blur-xl rounded-xl flex items-center justify-center transition-all mt-4"
                  style={{ 
                    background: 'rgba(2,4,10,0.7)',
                    border: `1px solid ${theme.colors.accent}40`,
                    color: theme.colors.accent,
                    boxShadow: `0 0 15px ${theme.colors.accent}15`
                  }}
                  data-testid="camera-fullscreen-btn"
                >
                  <Maximize className="w-5 h-5" />
                </motion.button>

                {/* Voice Commands Button */}
                <VoiceCommands
                  onCapture={captureFrame}
                  onStartRecording={startRecording}
                  onStopRecording={stopRecording}
                  isRecording={isRecording}
                  onZoomIn={zoomIn}
                  onZoomOut={zoomOut}
                  setZoomLevel={setZoomLevel}
                  zoomLevel={zoomLevel}
                  MAX_ZOOM={MAX_ZOOM}
                  toggleFilter={toggleFilter}
                  filters={filters}
                  onAnalyze={analyzeImage}
                  onFullscreen={() => setIsFullscreen(true)}
                  onSwitchCamera={switchCamera}
                  onReset={resetAnalysis}
                  isPremium={isPremium}
                  isOwner={isOwner}
                />

                {/* Gesture Controls Toggle */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setGesturesEnabled(!gesturesEnabled);
                    toast.info(gesturesEnabled ? 'Gestures OFF' : 'Gestures ON');
                  }}
                  className="p-3 rounded-2xl transition-all"
                  style={{
                    background: gesturesEnabled 
                      ? `linear-gradient(145deg, ${theme.colors.primary}30, ${theme.colors.accent}20)` 
                      : 'linear-gradient(145deg, rgba(100,110,130,1), rgba(60,70,90,1))',
                    boxShadow: gesturesEnabled 
                      ? `0 4px 20px ${theme.colors.primary}40, inset 0 2px 4px rgba(255,255,255,0.3)` 
                      : '0 4px 15px rgba(0,0,0,0.4), inset 0 2px 3px rgba(255,255,255,0.2)',
                    border: gesturesEnabled 
                      ? `2px solid ${theme.colors.primary}50` 
                      : '2px solid rgba(255,255,255,0.15)'
                  }}
                  data-testid="gesture-toggle-btn"
                >
                  <Hand className="w-5 h-5" style={{ color: gesturesEnabled ? theme.colors.primary : 'rgba(255,255,255,0.7)' }} />
                </motion.button>

                {/* Play/Pause Camera Button */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={toggleCameraPause}
                  className="p-3 rounded-2xl transition-all mt-2"
                  style={{
                    background: isCameraPaused 
                      ? 'linear-gradient(145deg, rgba(34,197,94,0.3), rgba(22,163,74,0.2))' 
                      : 'linear-gradient(145deg, rgba(100,110,130,1), rgba(60,70,90,1))',
                    boxShadow: isCameraPaused 
                      ? '0 4px 20px rgba(34,197,94,0.4), inset 0 2px 4px rgba(255,255,255,0.3)' 
                      : '0 4px 15px rgba(0,0,0,0.4), inset 0 2px 3px rgba(255,255,255,0.2)',
                    border: isCameraPaused 
                      ? '2px solid rgba(34,197,94,0.5)' 
                      : '2px solid rgba(255,255,255,0.15)'
                  }}
                  data-testid="camera-pause-btn"
                >
                  {isCameraPaused ? (
                    <Play className="w-5 h-5 text-green-400" />
                  ) : (
                    <Pause className="w-5 h-5 text-white/70" />
                  )}
                </motion.button>
                
                {/* Zoom Controls - Vertical along right edge */}
                <div className="flex flex-col gap-1 mt-2">
                  <button
                    onClick={() => applyZoom(Math.min(zoomLevel * 1.5, MAX_ZOOM))}
                    className="w-10 h-10 rounded-lg flex items-center justify-center transition-all active:scale-90"
                    style={{ 
                      background: 'rgba(0,0,0,0.7)',
                      border: '1px solid rgba(0,242,234,0.3)',
                      backdropFilter: 'blur(8px)'
                    }}
                  >
                    <Plus className="w-5 h-5 text-cyan-400" />
                  </button>
                  <div 
                    className="w-10 h-10 rounded-lg flex flex-col items-center justify-center"
                    style={{ 
                      background: 'rgba(0,0,0,0.7)',
                      border: '1px solid rgba(0,242,234,0.2)',
                      backdropFilter: 'blur(8px)'
                    }}
                  >
                    <span 
                      className="font-mono text-xs font-bold"
                      style={{ color: zoomLevel >= 10 ? '#00f2ea' : '#fff' }}
                    >
                      {zoomLevel.toFixed(1)}x
                    </span>
                    {activeCameraModule && (
                      <span className="text-[8px] font-mono text-cyan-400/70 uppercase">
                        {activeCameraModule.type === 'telephoto' ? 'TELE' : 
                         activeCameraModule.type === 'ultrawide' ? 'ULTRA' : 
                         activeCameraModule.type === 'wide' ? 'WIDE' : ''}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => applyZoom(Math.max(zoomLevel / 1.5, 1))}
                    className="w-10 h-10 rounded-lg flex items-center justify-center transition-all active:scale-90"
                    style={{ 
                      background: 'rgba(0,0,0,0.7)',
                      border: '1px solid rgba(0,242,234,0.3)',
                      backdropFilter: 'blur(8px)'
                    }}
                  >
                    <Minus className="w-5 h-5 text-cyan-400" />
                  </button>
                  <button
                    onClick={() => {
                      const newMoonMode = !isMoonModeActive;
                      if (newMoonMode) {
                        moonModeDefaultZoom.current = zoomLevel;
                        applyZoom(MAX_ZOOM); // Max native zoom for moon
                        setAiClarityEnabled(true);
                        setFilters(prev => ({ ...prev, night_mode: true }));
                        toast.success('🌙 Moon Mode ON!');
                      } else {
                        applyZoom(moonModeDefaultZoom.current || 1);
                        setAiClarityEnabled(false);
                        setFilters(prev => ({ ...prev, night_mode: false }));
                        toast.info('🌙 Moon Mode OFF');
                      }
                      setIsMoonModeActive(newMoonMode);
                    }}
                    className="w-10 h-10 rounded-lg flex items-center justify-center transition-all active:scale-90"
                    style={{ 
                      background: isMoonModeActive ? 'rgba(139,92,246,0.5)' : 'rgba(0,0,0,0.7)',
                      border: isMoonModeActive ? '2px solid #8b5cf6' : '1px solid rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(8px)',
                      boxShadow: isMoonModeActive ? '0 0 15px rgba(139,92,246,0.5)' : 'none'
                    }}
                    title="Moon Mode"
                    data-testid="moon-mode-btn"
                  >
                    <span className="text-base">🌙</span>
                  </button>
                  
                  {/* Meteor Watch Mode */}
                  <button
                    onClick={() => {
                      const newMeteorMode = !isMeteorModeActive;
                      if (newMeteorMode) {
                        meteorModeDefaultZoom.current = zoomLevel;
                        applyZoom(1); // Wide angle for meteors
                        setFilters(prev => ({ ...prev, night_mode: true, star_enhancement: isPremium || isOwner }));
                        toast.success('☄️ Meteor Watch ON!');
                      } else {
                        applyZoom(meteorModeDefaultZoom.current || 1);
                        setFilters(prev => ({ ...prev, night_mode: false, star_enhancement: false }));
                        toast.info('☄️ Meteor Watch OFF');
                      }
                      setIsMeteorModeActive(newMeteorMode);
                    }}
                    className="w-10 h-10 rounded-lg flex items-center justify-center transition-all active:scale-90"
                    style={{ 
                      background: isMeteorModeActive ? 'rgba(255,165,0,0.5)' : 'rgba(0,0,0,0.7)',
                      border: isMeteorModeActive ? '2px solid #ffa500' : '1px solid rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(8px)',
                      boxShadow: isMeteorModeActive ? '0 0 15px rgba(255,165,0,0.5)' : 'none'
                    }}
                    title="Meteor Watch"
                    data-testid="meteor-mode-btn"
                  >
                    <span className="text-base">☄️</span>
                  </button>
                  
                  {/* ISS Tracker Mode */}
                  <button
                    onClick={() => {
                      const newISSMode = !isISSModeActive;
                      if (newISSMode) {
                        issModeDefaultZoom.current = zoomLevel;
                        applyZoom(5); // Medium zoom for ISS
                        setFilters(prev => ({ ...prev, night_mode: true }));
                        toast.success('🛰️ ISS Tracker ON!');
                      } else {
                        applyZoom(issModeDefaultZoom.current || 1);
                        setFilters(prev => ({ ...prev, night_mode: false }));
                        toast.info('🛰️ ISS Tracker OFF');
                      }
                      setIsISSModeActive(newISSMode);
                    }}
                    className="w-10 h-10 rounded-lg flex items-center justify-center transition-all active:scale-90"
                    style={{ 
                      background: isISSModeActive ? 'rgba(0,191,255,0.5)' : 'rgba(0,0,0,0.7)',
                      border: isISSModeActive ? '2px solid #00bfff' : '1px solid rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(8px)',
                      boxShadow: isISSModeActive ? '0 0 15px rgba(0,191,255,0.5)' : 'none'
                    }}
                    title="ISS Tracker"
                    data-testid="iss-mode-btn"
                  >
                    <span className="text-base">🛰️</span>
                  </button>
                </div>
              </div>

              {/* Screen Adjustments Button - Top Left Corner */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAdjustments(!showAdjustments)}
                className="absolute left-4 top-4 z-30 p-2 rounded-full transition-all"
                style={{
                  background: showAdjustments 
                    ? `linear-gradient(145deg, ${theme.colors.primary}, ${theme.colors.accent})` 
                    : 'rgba(0,0,0,0.6)',
                  border: `1px solid ${showAdjustments ? theme.colors.primary : 'rgba(255,255,255,0.2)'}`,
                  boxShadow: showAdjustments ? `0 0 15px ${theme.colors.primary}50` : 'none'
                }}
                data-testid="adjustments-btn"
              >
                <SlidersHorizontal className="w-4 h-4" style={{ color: showAdjustments ? '#000' : '#fff' }} />
              </motion.button>

              {/* Compact Screen Adjustments Panel - Top Left */}
              <AnimatePresence>
                {showAdjustments && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 400 }}
                    className="absolute top-14 left-4 z-40 w-48 rounded-xl overflow-hidden"
                    style={{
                      background: 'rgba(0,0,0,0.9)',
                      border: `1px solid ${theme.colors.primary}30`,
                      backdropFilter: 'blur(15px)'
                    }}
                  >
                    <div className="p-3">
                      <p className="text-white/80 text-xs font-bold mb-2">Adjustments</p>
                      
                      {/* Brightness */}
                      <div className="mb-2">
                        <div className="flex justify-between text-[10px] text-white/60 mb-0.5">
                          <span>Brightness</span>
                          <span>{screenAdjustments.brightness}%</span>
                        </div>
                        <input 
                          type="range" 
                          min="50" 
                          max="150" 
                          value={screenAdjustments.brightness}
                          onChange={(e) => setScreenAdjustments(prev => ({ ...prev, brightness: parseInt(e.target.value) }))}
                          className="w-full h-1.5 rounded appearance-none cursor-pointer"
                          style={{ background: `linear-gradient(to right, #333, ${theme.colors.primary})`, accentColor: theme.colors.primary }}
                        />
                      </div>

                      {/* Contrast */}
                      <div className="mb-2">
                        <div className="flex justify-between text-[10px] text-white/60 mb-0.5">
                          <span>Contrast</span>
                          <span>{screenAdjustments.contrast}%</span>
                        </div>
                        <input 
                          type="range" 
                          min="50" 
                          max="150" 
                          value={screenAdjustments.contrast}
                          onChange={(e) => setScreenAdjustments(prev => ({ ...prev, contrast: parseInt(e.target.value) }))}
                          className="w-full h-1.5 rounded appearance-none cursor-pointer"
                          style={{ background: `linear-gradient(to right, #333, ${theme.colors.primary})`, accentColor: theme.colors.primary }}
                        />
                      </div>

                      {/* Saturation */}
                      <div className="mb-2">
                        <div className="flex justify-between text-[10px] text-white/60 mb-0.5">
                          <span>Saturation</span>
                          <span>{screenAdjustments.saturation}%</span>
                        </div>
                        <input 
                          type="range" 
                          min="0" 
                          max="200" 
                          value={screenAdjustments.saturation}
                          onChange={(e) => setScreenAdjustments(prev => ({ ...prev, saturation: parseInt(e.target.value) }))}
                          className="w-full h-1.5 rounded appearance-none cursor-pointer"
                          style={{ background: `linear-gradient(to right, #333, ${theme.colors.primary})`, accentColor: theme.colors.primary }}
                        />
                      </div>

                      {/* Reset */}
                      <button
                        onClick={() => setScreenAdjustments({ brightness: 100, contrast: 100, saturation: 100, exposure: 50 })}
                        className="w-full py-1.5 mt-1 rounded-lg text-[10px] font-bold"
                        style={{ 
                          background: `${theme.colors.primary}15`, 
                          color: theme.colors.primary,
                          border: `1px solid ${theme.colors.primary}30` 
                        }}
                      >
                        Reset
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Recording Indicator - Enhanced */}
              {isRecording && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="absolute top-20 lg:top-4 left-4 z-30 flex items-center gap-3 px-4 py-2 rounded-full backdrop-blur-xl"
                  style={{ 
                    background: 'rgba(255,46,46,0.2)',
                    border: '1px solid rgba(255,46,46,0.5)',
                    boxShadow: '0 0 20px rgba(255,46,46,0.3)'
                  }}
                >
                  <motion.div 
                    className="w-3 h-3 bg-[#ff2e2e] rounded-full"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  <span className="font-mono text-white font-bold text-sm tracking-wider">
                    REC {formatTime(recordingDuration)}
                  </span>
                </motion.div>
              )}

              {/* Timelapse Indicator - Enhanced */}
              {isTimelapseMode && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="absolute top-20 lg:top-4 left-4 z-30 flex items-center gap-3 px-4 py-2 rounded-full backdrop-blur-xl"
                  style={{ 
                    background: 'rgba(168,85,247,0.2)',
                    border: '1px solid rgba(168,85,247,0.5)',
                    boxShadow: '0 0 20px rgba(168,85,247,0.3)'
                  }}
                >
                  <RefreshCw className="w-4 h-4 text-[#a855f7] animate-spin" />
                  <span className="font-mono text-white font-bold text-sm tracking-wider">
                    TIMELAPSE • {timelapseFrameCount} frames
                  </span>
                </motion.div>
              )}

              {mode === "camera" && !currentImage && (
                <div className="relative w-full h-full overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    style={{ 
                      // Native camera zoom only - no CSS scaling
                      ...liveFilterStyle,
                      imageRendering: 'high-quality',
                      filter: `brightness(${screenAdjustments.brightness}%) contrast(${screenAdjustments.contrast}%) saturate(${screenAdjustments.saturation}%)${activeFilters.length > 0 ? ` ${liveFilterStyle?.filter || ''}` : ''}`,
                    }}
                  />

                  {/* Live AI HUD + Motion Heatmap Overlay */}
                  {isLiveAIOn && (
                    <div className="absolute top-4 right-4 z-30 space-y-2">
                      <div
                        className="px-3 py-2 rounded-xl backdrop-blur-xl"
                        style={{
                          background: 'rgba(0,0,0,0.55)',
                          border: `1px solid ${theme.colors.primary}30`
                        }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-mono text-xs" style={{ color: theme.colors.primary }}>
                            LIVE AI
                          </div>
                          <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: theme.colors.primary }} />
                        </div>
                        {lastLiveAIResult && (
                          <div className="mt-1">
                            <div className="font-exo2 text-xs text-white/90 line-clamp-2">
                              {lastLiveAIResult.analysis}
                            </div>
                            <div className="mt-1 flex items-center justify-between text-xs font-mono text-white/70">
                              <span>Anoms: {lastLiveAIResult.anomalies_detected?.length || 0}</span>
                              <span>Conf: {Math.round(lastLiveAIResult.confidence_score || 0)}%</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {motionHeatmapDataUrl && (
                        <div
                          className="rounded-xl overflow-hidden"
                          style={{
                            background: 'rgba(0,0,0,0.25)',
                            border: '1px solid rgba(255,255,255,0.12)'
                          }}
                        >
                          <img
                            src={motionHeatmapDataUrl}
                            alt="Motion heatmap"
                            className="w-40 h-24 object-cover opacity-80"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Anomaly Tracking Overlay */}
                  {isAnomalyTrackingOn && (
                    <>
                      {/* Full-screen tracking overlay image */}
                      {anomalyOverlayUrl && (
                        <img
                          src={anomalyOverlayUrl}
                          alt="Tracking overlay"
                          className="absolute inset-0 w-full h-full object-cover pointer-events-none z-20"
                          style={{ 
                            opacity: 0.7,
                            mixBlendMode: 'screen'
                          }}
                        />
                      )}
                      
                      {/* Tracking HUD */}
                      <div className="absolute top-20 lg:top-4 left-4 z-30 space-y-2">
                        <div
                          className="px-3 py-2 rounded-xl backdrop-blur-xl"
                          style={{
                            background: 'rgba(0,0,0,0.55)',
                            border: '1px solid rgba(34,197,94,0.3)'
                          }}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-mono text-xs text-green-400">
                              🎯 AI TRACKING
                            </div>
                            <div className="w-2 h-2 rounded-full animate-pulse bg-green-400" />
                          </div>
                          <div className="mt-1 font-mono text-xs text-white/80">
                            {trackedAnomalies.length} object{trackedAnomalies.length !== 1 ? 's' : ''} locked
                          </div>
                          {frameAnalysis && (
                            <div className="mt-1 font-exo2 text-[10px] text-slate-300 line-clamp-2">
                              {frameAnalysis}
                            </div>
                          )}
                        </div>
                        
                        {/* Object list */}
                        {trackedAnomalies.length > 0 && (
                          <div
                            className="px-2 py-1.5 rounded-lg backdrop-blur-xl max-h-32 overflow-y-auto"
                            style={{
                              background: 'rgba(0,0,0,0.4)',
                              border: '1px solid rgba(255,255,255,0.1)'
                            }}
                          >
                            {trackedAnomalies.slice(0, 5).map((obj, i) => (
                              <div key={i} className="flex items-center justify-between py-0.5 text-[9px] font-mono">
                                <span style={{ 
                                  color: obj.confidence > 0.7 ? '#4ade80' : obj.confidence > 0.4 ? '#fbbf24' : '#f87171' 
                                }}>
                                  {obj.label}
                                </span>
                                <span className="text-slate-400">{Math.round(obj.confidence * 100)}%</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}


                  {/* Live Filter Overlays */}
                  {filterOverlays.map((overlay, index) => (
                    <div
                      key={index}
                      className="absolute inset-0 pointer-events-none"
                      style={overlay.style}
                    />
                  ))}
                  {/* Active Filters Indicator */}
                  {activeFilters.length > 0 && (
                    <div 
                      className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-2 z-30"
                      style={{ 
                        background: theme.colors.glass,
                        border: `1px solid ${theme.colors.primary}50`,
                        color: theme.colors.primary
                      }}
                    >
                      <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: theme.colors.primary }} />
                      {activeFilters.length} filter{activeFilters.length > 1 ? 's' : ''} live
                    </div>
                  )}

                  {/* AI Zoom Clarity Indicator */}
                  {zoomLevel >= 5 && aiClarityEnabled && (
                    <div 
                      className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-3 z-30"
                      style={{ 
                        background: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(168,85,247,0.4)',
                        boxShadow: '0 0 20px rgba(168,85,247,0.2)'
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-400" />
                        <span className="font-mono text-purple-300">AI CLARITY</span>
                      </div>
                      <div className="font-unbounded text-lg text-white">{zoomLevel.toFixed(0)}x</div>
                      {clarityDetails && (
                        <div className="flex items-center gap-2 text-[10px] font-mono text-purple-200/70">
                          <span>Sharp: {clarityDetails.sharpness}x</span>
                          <span>•</span>
                          <span>Contrast: {clarityDetails.contrast}x</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Real-time Analysis Overlay */}
                  {isRealTimeMode && realTimeAnalysis && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="absolute bottom-4 right-4 max-w-xs p-3 rounded-xl backdrop-blur-xl z-30"
                      style={{ 
                        background: 'rgba(2,4,10,0.8)',
                        border: `1px solid ${theme.colors.primary}30`
                      }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="w-4 h-4 text-cyan-400 animate-pulse" />
                        <span className="font-mono text-xs text-cyan-400 uppercase tracking-wider">Live AI Analysis</span>
                      </div>
                      <p className="font-exo2 text-xs text-white line-clamp-3">
                        {realTimeAnalysis.analysis?.substring(0, 150)}...
                      </p>
                      {realTimeAnalysis.anomalies_detected?.length > 0 && (
                        <div className="mt-2 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 text-yellow-400" />
                          <span className="font-mono text-xs text-yellow-400">
                            {realTimeAnalysis.anomalies_detected.length} anomalies
                          </span>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Celestial Overlay */}
                  {showCelestialOverlay && celestialData && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute top-20 lg:top-4 left-4 max-w-xs p-3 rounded-xl backdrop-blur-xl z-30"
                      style={{ 
                        background: 'rgba(2,4,10,0.8)',
                        border: '1px solid rgba(255,215,0,0.3)'
                      }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-4 h-4 text-yellow-400" />
                        <span className="font-mono text-xs text-yellow-400 uppercase tracking-wider">Celestial Data</span>
                      </div>
                      <div className="space-y-1">
                        <p className="font-exo2 text-xs text-white flex items-center gap-2">
                          <Moon className="w-3 h-3 text-slate-300" />
                          {celestialData.moon_phase}
                        </p>
                        <p className="font-exo2 text-xs text-slate-400">
                          {celestialData.darkness_level} • {celestialData.visible_constellations?.slice(0,3).join(', ')}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {celestialData.celestial_objects?.slice(0, 4).map((obj, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] rounded font-mono">
                              {obj.name}

              {/* Live AI action button is in the main action row below */}

                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}

              {mode === "upload" && !currentImage && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="w-full h-full flex flex-col items-center justify-center cursor-pointer transition-colors group"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                  
                  {/* Upload Icon with Glow */}
                  <motion.div
                    className="relative mb-6"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <div 
                      className="absolute inset-0 blur-2xl opacity-30"
                      style={{ background: theme.colors.primary }}
                    />
                    <div 
                      className="relative p-6 rounded-2xl backdrop-blur-xl"
                      style={{ 
                        background: `${theme.colors.primary}10`,
                        border: `2px dashed ${theme.colors.primary}40`
                      }}
                    >
                      <Upload className="w-12 h-12" style={{ color: theme.colors.primary }} />
                    </div>
                  </motion.div>
                  
                  <p className="font-unbounded font-bold text-lg text-white mb-2">Upload Sky Image</p>
                  <p className="font-exo2 text-slate-400 text-center text-sm">
                    Tap to select an image for AI analysis
                  </p>
                  <p className="font-mono text-xs text-slate-600 mt-3 tracking-wider">
                    JPG • PNG • WEBP
                  </p>
                </motion.div>
              )}

              {currentImage && (
                <div className="relative w-full h-full">
                  <img
                    src={currentImage}
                    alt="Captured/Uploaded"
                    className="w-full h-full object-contain"
                    style={{ 
                      transform: `scale(${zoomLevel})`,
                      transformOrigin: 'center center',
                      ...liveFilterStyle,
                      // CRYSTAL CLEAR - Maximum sharpness for zoomed images
                      imageRendering: zoomLevel > 2 ? 'crisp-edges' : 'high-quality',
                      WebkitImageRendering: zoomLevel > 2 ? 'crisp-edges' : 'auto',
                      // CSS Sharpening enhancement for high zoom
                      filter: `${liveFilterStyle?.filter || 'none'} ${zoomLevel > 5 ? 'contrast(1.05) saturate(1.02)' : ''} ${zoomLevel > 20 ? 'brightness(1.02)' : ''}`.trim(),
                      transition: 'filter 0.3s ease, transform 0.3s ease'
                    }}
                    data-testid="preview-image"
                  />
                  {/* Live Filter Overlays on Image */}
                  {filterOverlays.map((overlay, index) => (
                    <div
                      key={index}
                      className="absolute inset-0 pointer-events-none"
                      style={overlay.style}
                    />
                  ))}
                </div>
              )}

              {/* Hidden canvas for capture */}
              <canvas ref={canvasRef} className="hidden" />

              {/* Loading Overlay */}
              <AnimatePresence>
                {isAnalyzing && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-[#02040a]/80 flex flex-col items-center justify-center z-30"
                  >
                    <Loader2 className="w-12 h-12 text-[#00f2ea] animate-spin mb-4" />
                    <p className="font-exo2 text-white">Analyzing sky...</p>
                    <p className="font-mono text-xs text-slate-500 mt-2">GPT-4o Vision Processing</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Action Buttons - Desktop */}
            <div className="hidden lg:flex flex-wrap gap-3 mt-4">
              {mode === "camera" && !capturedImage && (
                <>
                  <button
                    data-testid="capture-btn"
                    onClick={captureFrame}
                    className="btn-primary flex-1 sm:flex-none"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Camera className="w-5 h-5" />
                      Capture Frame
                    </span>
                  </button>

                  {/* Recording Button */}
                  {!isRecording ? (
                    <button
                      data-testid="record-btn"
                      onClick={startRecording}
                      className="flex-1 sm:flex-none px-6 py-3 bg-[#ff2e2e] hover:bg-[#ff4444] text-white font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2 transition-colors"
                    >
                      <div className="w-3 h-3 bg-white rounded-full" />
                      Record HD
                      {!isPremium && !isOwner && <Crown className="w-4 h-4 text-[#ffd700]" />}
                    </button>
                  ) : (
                    <button
                      data-testid="stop-record-btn"
                      onClick={stopRecording}
                      className="flex-1 sm:flex-none px-6 py-3 bg-[#ff2e2e] text-white font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2 animate-pulse"
                    >
                      <div className="w-3 h-3 bg-white rounded-sm" />
                      Stop {formatTime(recordingDuration)}
                    </button>
                  )}

                  {/* Timelapse Button */}
                  {!isTimelapseMode ? (
                    <button
                      data-testid="timelapse-btn"
                      onClick={startTimelapse}
                      className="flex-1 sm:flex-none px-6 py-3 bg-[#a855f7] hover:bg-[#9333ea] text-white font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2 transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Timelapse
                      {!isPremium && !isOwner && <Crown className="w-4 h-4 text-[#ffd700]" />}
                    </button>
                  ) : (
                    <button
                      data-testid="stop-timelapse-btn"
                      onClick={stopTimelapse}
                      className="flex-1 sm:flex-none px-6 py-3 bg-[#a855f7] text-white font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2 animate-pulse"
                    >
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      {timelapseFrameCount} frames
                    </button>
                  )}

                  {/* Real-time AI Analysis Button */}
                  {!isRealTimeMode ? (
                    <button
                      data-testid="realtime-btn"
                      onClick={startRealTimeAnalysis}
                      className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2 transition-all"
                    >
                      <Activity className="w-4 h-4" />
                      Live AI
                      {!isPremium && !isOwner && <Crown className="w-4 h-4 text-[#ffd700]" />}
                    </button>
                  ) : (
                    <button
                      data-testid="stop-realtime-btn"
                      onClick={stopRealTimeAnalysis}
                      className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2 animate-pulse"
                    >
                      <Activity className="w-4 h-4 animate-pulse" />
                      Stop Live
                    </button>
                  )}

                  {/* Celestial Overlay Toggle */}
                  <button
                    data-testid="celestial-btn"
                    onClick={toggleCelestialOverlay}
                    className={`flex-1 sm:flex-none px-6 py-3 font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2 transition-all ${
                      showCelestialOverlay 
                        ? 'bg-yellow-500 text-black' 
                        : 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30'
                    }`}
                  >
                    <Star className="w-4 h-4" />
                    Stars
                  </button>
                </>
              )}

              {mode === "upload" && !uploadedImage && (
                <button
                  data-testid="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-primary flex-1 sm:flex-none"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Upload className="w-5 h-5" />
                    Select Image
                  </span>
                </button>
              )}

              {isLiveCameraView && (
                <>
                  <button
                    data-testid="apply-filters-btn"
                    onClick={applyFilters}
                    disabled={isAnalyzing || activeFilters.length === 0}
                    className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="w-5 h-5" />
                    Apply Filters
                  </button>


              {/* Live AI toggle */}
              <button
                data-testid="analyze-btn"
                onClick={toggleLiveAI}
                disabled={isAnalyzing && !isLiveAIOn}
                className={`btn-primary flex-1 sm:flex-none disabled:opacity-50 ${isLiveAIOn ? 'bg-red-500 hover:bg-red-600' : ''}`}
              >
                <span className="flex items-center justify-center gap-2">
                  <Scan className="w-5 h-5" />
                  {isLiveAIOn ? 'Stop Live AI' : 'Start Live AI'}
                </span>
              </button>

                  <button
                    data-testid="ai-enhance-btn"
                    onClick={applyAIFilters}
                    disabled={isAnalyzing}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold disabled:opacity-50 transition-all"
                    style={{
                      background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                      color: '#fff',
                      boxShadow: '0 4px 15px rgba(168,85,247,0.4)'
                    }}
                  >
                    <Cpu className="w-5 h-5" />
                    AI Enhance
                  </button>

                  {/* Live AI action button is rendered above for live camera feed */}

                  {enhancedImage && (
                    <button
                      data-testid="download-btn"
                      onClick={downloadImage}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <Download className="w-5 h-5" />
                      Download
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Simple Floating Capture Controls - Mobile Only */}
            <div className="lg:hidden absolute bottom-6 left-1/2 transform -translate-x-1/2 z-40">
              <div className="flex items-center gap-3">
                {/* Fullscreen Button */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsFullscreen(true)}
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{
                    background: 'rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                  data-testid="fullscreen-btn"
                >
                  <Maximize className="w-4 h-4 text-white" />
                </motion.button>

                {/* Mode Toggle Pills */}
                <div className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)' }}>
                  <button
                    onClick={() => setMode('camera')}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${mode === 'camera' ? 'bg-white text-black' : 'text-white/60'}`}
                  >
                    Photo
                  </button>
                  <button
                    onClick={() => setMode('video')}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${mode === 'video' ? 'bg-red-500 text-white' : 'text-white/60'}`}
                  >
                    Video
                  </button>
                </div>

                {/* Main Capture Button */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    if (mode === 'video') {
                      if (isRecording) {
                        stopRecording();
                      } else {
                        startRecording();
                      }
                    } else {
                      captureFrame();
                    }
                  }}
                  className="relative w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: mode === 'video' 
                      ? (isRecording ? 'linear-gradient(145deg, #ff4444, #cc0000)' : 'linear-gradient(145deg, #ff6b6b, #ee5555)')
                      : 'linear-gradient(145deg, #ffffff, #e0e0e0)',
                    boxShadow: isRecording 
                      ? '0 0 30px rgba(255,0,0,0.5), inset 0 2px 4px rgba(255,255,255,0.3)'
                      : '0 4px 20px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.5)',
                    border: '4px solid rgba(255,255,255,0.3)'
                  }}
                  data-testid="main-capture-btn"
                >
                  {mode === 'video' ? (
                    isRecording ? (
                      <div className="w-5 h-5 bg-white rounded-sm" />
                    ) : (
                      <div className="w-4 h-4 bg-white rounded-full" />
                    )
                  ) : (
                    <Camera className="w-6 h-6 text-gray-800" />
                  )}
                </motion.button>

                {/* Analyze Button */}
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={analyzeImage}
                  disabled={isAnalyzing || (!capturedImage && !uploadedImage)}
                  className="px-4 py-2 rounded-full text-xs font-bold transition-all disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(145deg, rgba(168,85,247,0.8), rgba(139,92,246,0.8))',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 15px rgba(168,85,247,0.3)'
                  }}
                  data-testid="analyze-btn"
                >
                  {isAnalyzing ? '...' : '✨ AI'}
                </motion.button>
              </div>

              {/* Recording Duration */}
              {isRecording && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full flex items-center gap-2"
                  style={{ background: 'rgba(255,0,0,0.8)' }}
                >
                  <motion.div
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="w-2 h-2 bg-white rounded-full"
                  />
                  <span className="text-white text-xs font-mono">{formatTime(recordingDuration)}</span>
                </motion.div>
              )}
            </div>
            </>
            )}
          </div>
        </div>

        {/* Right Panel - Pro 3D Control System */}
        <aside 
          className={`${isDesktop ? 'flex' : 'hidden'} w-96 xl:w-[420px] flex-col overflow-hidden relative z-10`}
          style={{ 
            background: 'linear-gradient(180deg, rgba(12,18,32,0.62) 0%, rgba(6,10,22,0.7) 100%)',
            backdropFilter: 'blur(18px) saturate(140%)',
            WebkitBackdropFilter: 'blur(18px) saturate(140%)',
            borderLeft: '1px solid rgba(255,255,255,0.06)',
            boxShadow: '-10px 0 40px rgba(0,0,0,0.3)'
          }}
        >
          {/* Pro 3D Control Panel - Filters & Zoom */}
          <Pro3DControlPanel
            filters={filters}
            toggleFilter={toggleFilter}
            isPremium={isPremium}
            isOwner={isOwner}
            zoomLevel={zoomLevel}
            setZoomLevel={setZoomLevel}
            MAX_ZOOM={MAX_ZOOM}
            activeFiltersCount={activeFilters.length}
            // AI Features
            aiClarityEnabled={aiClarityEnabled}
            onToggleAIClarity={() => setAiClarityEnabled(!aiClarityEnabled)}
            clarityDetails={clarityDetails}
            isAnomalyTrackingOn={isAnomalyTrackingOn}
            onToggleAnomalyTracking={toggleAnomalyTracking}
            trackingSensitivity={trackingSensitivity}
            onSetTrackingSensitivity={setTrackingSensitivity}
            trackedAnomalies={trackedAnomalies}
            frameAnalysis={frameAnalysis}
          />
        </aside>

        {/* Analysis Results Panel - Separate floating panel on desktop */}
        <AnimatePresence>
          {analysisResult && (
            <motion.aside
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className={`${isDesktop ? 'block' : 'hidden'} fixed right-4 top-24 bottom-24 w-80 overflow-y-auto rounded-2xl z-30`}
              style={{
                background: 'linear-gradient(180deg, rgba(10,15,30,0.95) 0%, rgba(2,4,10,0.98) 100%)',
                border: '1px solid rgba(0,242,234,0.2)',
                boxShadow: '0 0 40px rgba(0,0,0,0.5), 0 0 20px rgba(0,242,234,0.1)'
              }}
            >
              <div className="p-4">
                <h3 className="font-unbounded font-bold text-lg mb-4 flex items-center gap-2" style={{ color: theme.colors.primary }}>
                  <Scan className="w-5 h-5" />
                  Analysis Results
                </h3>

                {/* Confidence Score */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs text-slate-500 uppercase">Confidence</span>
                    <span className="font-unbounded font-bold text-[#00f2ea]">
                      {analysisResult.confidence_score.toFixed(1)}%
                    </span>
                  </div>
                  <div className="confidence-meter">
                    <div 
                      className="confidence-meter-fill"
                      style={{ 
                        width: `${analysisResult.confidence_score}%`,
                        background: analysisResult.confidence_score > 70 
                          ? '#00f2ea' 
                          : analysisResult.confidence_score > 40 
                            ? '#ffd700' 
                            : '#ff2e2e'
                      }}
                    />
                  </div>
                </div>

                {/* Anomalies Detected */}
                {analysisResult.anomalies_detected.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-exo2 font-medium text-[#ff2e2e] mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Anomalies Detected
                    </h4>
                    <div className="space-y-2">
                      {analysisResult.anomalies_detected.map((anomaly, i) => (
                        <div key={i} className="anomaly-item">
                          <p className="font-exo2 text-sm text-white">{anomaly}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Full Analysis */}
                <div>
                  <h4 className="font-exo2 font-medium text-slate-300 mb-3">Full Analysis</h4>
                  <div className="bg-[#02040a] p-4 rounded-2xl max-h-64 overflow-y-auto">
                    <p className="font-mono text-xs text-slate-400 whitespace-pre-wrap">
                      {analysisResult.analysis}
                    </p>
                  </div>
                </div>

                {/* Filters Applied */}
                {analysisResult.filters_applied.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <span className="font-mono text-xs text-slate-500">Filters: </span>
                    <span className="font-mono text-xs text-[#00f2ea]">
                      {analysisResult.filters_applied.join(', ')}
                    </span>
                  </div>
                )}

                {/* Share Results Button */}
                <button
                  onClick={() => setShowShareModal(true)}
                  className="w-full mt-4 btn-secondary flex items-center justify-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Share Discovery
                </button>
              </div>
            </motion.aside>
            )}
          </AnimatePresence>
      </main>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#02040a]/90 backdrop-blur-sm overflow-y-auto"
            onClick={() => setShowPaymentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-panel max-w-md w-full p-6 my-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="payment-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-unbounded font-bold text-xl text-white flex items-center gap-2">
                  <Crown className="w-6 h-6 text-[#ffd700]" />
                  Upgrade to Premium
                </h2>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                  data-testid="close-payment-modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Features */}
              <div className="mb-6">
                <ul className="space-y-3">
                  {[
                    'Ultra HD Recording (8 Mbps)',
                    'AI Night Vision Filter',
                    'AI Infrared Filter',
                    'AI Thermal/Heat Vision',
                    'Motion Enhance (Meteors/UFOs)',
                    'Video Analysis (Frame-by-Frame)',
                    'X-Zoom Native Camera',
                    '4K AI Upscaling',
                    'Auto-Clarity Night Enhancement',
                    'Object Tracking & Highlighting',
                    'Timelapse Capture Mode',
                    'AI Video Ad Generator (Sora 2)'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 font-exo2 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-[#00f2ea]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Cash App Direct Payment - PRIMARY */}
              <div className="mb-6 p-4 bg-[#00D632]/10 border border-[#00D632]/30 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-[#00D632] rounded-2xl flex items-center justify-center">
                    <span className="text-white font-bold text-lg">$</span>
                  </div>
                  <span className="font-unbounded font-bold text-white">Pay with Cash App</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    onClick={() => handleUpgradeClick('cashapp_monthly')}
                    data-testid="cashapp-monthly-btn"
                    className="block p-3 bg-[#00D632] hover:bg-[#00B82B] text-white rounded-2xl text-center transition-colors"
                  >
                    <span className="font-bold block">Monthly</span>
                    <span className="text-sm opacity-90">$9.99</span>
                  </button>
                  <button
                    onClick={() => handleUpgradeClick('cashapp_yearly')}
                    data-testid="cashapp-yearly-btn"
                    className="block p-3 bg-[#00D632] hover:bg-[#00B82B] text-white rounded-2xl text-center transition-colors"
                  >
                    <span className="font-bold block">Yearly</span>
                    <span className="text-sm opacity-90">$99.99</span>
                  </button>
                </div>

                <div className="text-center">
                  <p className="font-mono text-xs text-[#00D632] mb-2">$AetherLens</p>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_skyguardian-1/artifacts/enmaz34n_IMG_1224.jpeg"
                    alt="Cash App QR Code"
                    className="w-32 h-32 mx-auto rounded-2xl"
                  />
                  <p className="font-mono text-xs text-slate-500 mt-2">Scan with Cash App</p>
                </div>
              </div>

              {/* After Payment - Enter Code */}
              <div className="p-4 bg-[#ffd700]/10 border border-[#ffd700]/20 rounded-2xl">
                <p className="font-mono text-xs text-[#ffd700] text-center mb-3">
                  After payment, enter the code from your confirmation
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={paymentCode}
                    onChange={(e) => {
                      setPaymentCode(e.target.value.toUpperCase());
                      setPaymentCodeError("");
                    }}
                    placeholder="Enter activation code"
                    className="flex-1 px-3 py-2 bg-[#02040a] border border-[#ffd700]/30 rounded-xl text-white font-mono text-sm placeholder:text-slate-500 focus:outline-none focus:border-[#ffd700]"
                    data-testid="payment-code-input"
                  />
                  <button
                    onClick={verifyPaymentCode}
                    className="px-4 py-2 bg-[#ffd700] text-[#02040a] font-bold rounded-xl text-sm hover:bg-[#ffed4a] transition-colors"
                    data-testid="verify-code-btn"
                  >
                    Activate
                  </button>
                </div>
                {paymentCodeError && (
                  <p className="text-red-400 text-xs mt-2 text-center">{paymentCodeError}</p>
                )}
                <p className="font-mono text-[10px] text-slate-500 mt-2 text-center">
                  Code sent to your email after Cash App payment
                </p>
              </div>

              {/* Loading State */}
              {isProcessingPayment && (
                <div className="absolute inset-0 bg-[#02040a]/80 flex items-center justify-center rounded-2xl">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 text-[#00f2ea] animate-spin mx-auto mb-2" />
                    <p className="font-exo2 text-white">Preparing checkout...</p>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stripe Payment Modal - Auto-activates premium on successful payment */}
      <StripePayment
        isOpen={showQuickPayment}
        onClose={() => setShowQuickPayment(false)}
        onPaymentComplete={() => {
          activatePremium();
          setShowQuickPayment(false);
        }}
      />

      {/* Analyze Capture Prompt Modal */}
      <AnimatePresence>
        {showAnalyzePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowAnalyzePrompt(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-sm rounded-2xl overflow-hidden"
              style={{ 
                background: 'linear-gradient(145deg, rgba(20,25,35,0.98), rgba(10,15,25,0.98))',
                border: `1px solid ${theme.colors.primary}40`,
                boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 40px ${theme.colors.primary}20`
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Preview */}
              {pendingCaptureForAnalysis && (
                <div className="relative h-48 overflow-hidden">
                  {pendingCaptureForAnalysis.startsWith('blob:') ? (
                    <video 
                      src={pendingCaptureForAnalysis} 
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                    />
                  ) : (
                    <img 
                      src={pendingCaptureForAnalysis} 
                      alt="Capture" 
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
              )}
              
              {/* Content */}
              <div className="p-5">
                <h3 className="font-unbounded font-bold text-lg text-white mb-2 text-center">
                  🔍 Analyze Capture?
                </h3>
                <p className="text-sm text-white/60 text-center mb-5">
                  Use AI to detect anomalies and identify celestial objects
                </p>
                
                {/* Buttons */}
                <div className="flex gap-3">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowAnalyzePrompt(false);
                      // Navigate to gallery
                      navigate('/gallery');
                    }}
                    className="flex-1 py-3 rounded-xl font-bold text-sm"
                    style={{ 
                      background: 'rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.7)'
                    }}
                  >
                    Skip
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={async () => {
                      setShowAnalyzePrompt(false);
                      // Start analysis
                      if (pendingCaptureForAnalysis) {
                        if (pendingCaptureForAnalysis.startsWith('blob:')) {
                          // Video analysis
                          setMode('video');
                          toast.info('Starting video analysis...');
                          // The video is already set in uploadedVideo state
                        } else {
                          // Image analysis - capturedImage is already set
                          toast.info('Starting AI analysis...');
                          // Trigger analysis
                          setTimeout(() => {
                            analyzeImage();
                          }, 500);
                        }
                      }
                    }}
                    className="flex-1 py-3 rounded-xl font-bold text-sm"
                    style={{ 
                      background: `linear-gradient(145deg, ${theme.colors.primary}, ${theme.colors.accent})`,
                      color: 'white',
                      boxShadow: `0 4px 15px ${theme.colors.primary}50`
                    }}
                  >
                    ✨ Analyze Now
                  </motion.button>
                </div>
                
                {/* Auto-save note */}
                <p className="text-[10px] text-white/40 text-center mt-3">
                  Capture saved to device gallery
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Star Map - Live Constellation & Celestial Body Finder */}
      <StarMap
        isOpen={showStarMap}
        onClose={() => setShowStarMap(false)}
      />

      {/* Night Sky Alerts - ISS Passes, Meteor Showers, etc. */}
      <NightSkyAlerts
        isOpen={showNightSkyAlerts}
        onClose={() => setShowNightSkyAlerts(false)}
      />

      {/* Email Collection Modal */}
      <AnimatePresence>
        {showEmailModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#02040a]/90 backdrop-blur-sm"
            onClick={() => setShowEmailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-panel max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <Mail className="w-12 h-12 text-[#00f2ea] mx-auto mb-4" />
                <h2 className="font-unbounded font-bold text-xl text-white">Enter Your Email</h2>
                <p className="font-exo2 text-slate-400 mt-2">For payment confirmation & updates</p>
              </div>

              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="your@email.com"
                className="input-default w-full mb-4"
                data-testid="email-input"
              />

              <button
                onClick={handleEmailSubmit}
                className="w-full btn-primary"
                data-testid="continue-payment-btn"
              >
                <span>Continue to Payment</span>
              </button>

              <p className="font-mono text-xs text-slate-500 text-center mt-4">
                We&apos;ll never spam you
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Owner Login Modal */}
      <AnimatePresence>
        {showOwnerModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#02040a]/90 backdrop-blur-sm"
            onClick={() => setShowOwnerModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-panel max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <Lock className="w-12 h-12 text-[#ffd700] mx-auto mb-4" />
                <h2 className="font-unbounded font-bold text-xl text-white">Owner Access</h2>
                <p className="font-exo2 text-slate-400 mt-2">Enter admin code</p>
              </div>

              <input
                type="password"
                value={ownerCode}
                onChange={(e) => setOwnerCode(e.target.value)}
                placeholder="Enter code..."
                className="input-default w-full mb-4"
                data-testid="owner-code-input"
                onKeyDown={(e) => e.key === 'Enter' && handleOwnerLogin()}
              />

              <button
                onClick={handleOwnerLogin}
                className="w-full btn-premium"
                data-testid="owner-login-btn"
              >
                <span className="flex items-center justify-center gap-2">
                  <Unlock className="w-5 h-5" />
                  Unlock
                </span>
              </button>

              {isOwner && (
                <button
                  onClick={() => {
                    setIsOwner(false);
                    setIsPremium(false);
                    localStorage.removeItem('aether_owner');
                    localStorage.removeItem('skybot_owner_mode');
                    setShowOwnerModal(false);
                    toast.success("Logged out of owner mode");
                  }}
                  className="w-full mt-3 btn-secondary"
                >
                  Logout Owner Mode
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#02040a]/90 backdrop-blur-sm"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-panel max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-unbounded font-bold text-xl text-white flex items-center gap-2">
                  <Share2 className="w-6 h-6 text-[#00f2ea]" />
                  Share Skybot
                </h2>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <p className="font-exo2 text-slate-300 mb-6">
                Help others discover what&apos;s really in the sky! Share on your favorite platform.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <a
                  href={shareLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackShare('twitter')}
                  className="flex items-center justify-center gap-2 p-3 bg-[#1DA1F2]/20 border border-[#1DA1F2]/30 rounded-2xl text-[#1DA1F2] hover:bg-[#1DA1F2]/30 transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                  Twitter/X
                </a>
                <a
                  href={shareLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackShare('facebook')}
                  className="flex items-center justify-center gap-2 p-3 bg-[#4267B2]/20 border border-[#4267B2]/30 rounded-2xl text-[#4267B2] hover:bg-[#4267B2]/30 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                  Facebook
                </a>
                <a
                  href={shareLinks.reddit}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackShare('reddit')}
                  className="flex items-center justify-center gap-2 p-3 bg-[#FF4500]/20 border border-[#FF4500]/30 rounded-2xl text-[#FF4500] hover:bg-[#FF4500]/30 transition-colors"
                >
                  <span className="font-bold">r/</span>
                  Reddit
                </a>
                <a
                  href={shareLinks.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackShare('whatsapp')}
                  className="flex items-center justify-center gap-2 p-3 bg-[#25D366]/20 border border-[#25D366]/30 rounded-2xl text-[#25D366] hover:bg-[#25D366]/30 transition-colors"
                >
                  WhatsApp
                </a>
                <a
                  href={shareLinks.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackShare('telegram')}
                  className="flex items-center justify-center gap-2 p-3 bg-[#0088cc]/20 border border-[#0088cc]/30 rounded-2xl text-[#0088cc] hover:bg-[#0088cc]/30 transition-colors col-span-2"
                >
                  Telegram
                </a>
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="input-default flex-1 text-sm"
                />
                <button
                  onClick={copyLink}
                  className="btn-secondary px-4"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Video Ad Generator Modal */}
      <AnimatePresence>
        {showAdGeneratorModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#02040a]/90 backdrop-blur-sm overflow-y-auto"
            onClick={() => setShowAdGeneratorModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-panel max-w-lg w-full p-6 my-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="ad-generator-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-unbounded font-bold text-xl text-white flex items-center gap-2">
                  <Film className="w-6 h-6 text-[#a855f7]" />
                  AI Video Ad Generator
                </h2>
                <button
                  onClick={() => setShowAdGeneratorModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {!generatedAdVideo ? (
                <>
                  <p className="font-exo2 text-slate-400 mb-6">
                    Create stunning advertisement videos for Skybot using AI-powered Sora 2 video generation.
                  </p>

                  {/* Prompt Input */}
                  <div className="mb-4">
                    <label className="font-mono text-xs text-slate-500 uppercase mb-2 block">
                      Video Prompt
                    </label>
                    <textarea
                      value={adPrompt}
                      onChange={(e) => setAdPrompt(e.target.value)}
                      placeholder="Describe your video ad..."
                      className="input-default w-full h-24 resize-none"
                      data-testid="ad-prompt-input"
                    />
                  </div>

                  {/* Duration Selection */}
                  <div className="mb-4">
                    <label className="font-mono text-xs text-slate-500 uppercase mb-2 block">
                      Duration
                    </label>
                    <div className="flex gap-2">
                      {[4, 8, 12].map(d => (
                        <button
                          key={d}
                          onClick={() => setAdDuration(d)}
                          className={`flex-1 py-2 rounded-2xl font-unbounded font-bold transition-colors ${
                            adDuration === d 
                              ? 'bg-[#a855f7] text-white' 
                              : 'bg-white/10 text-slate-400 hover:bg-white/20'
                          }`}
                        >
                          {d}s
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Size Selection */}
                  <div className="mb-6">
                    <label className="font-mono text-xs text-slate-500 uppercase mb-2 block">
                      Video Size
                    </label>
                    <select
                      value={adSize}
                      onChange={(e) => setAdSize(e.target.value)}
                      className="input-default w-full"
                      data-testid="ad-size-select"
                    >
                      <option value="1280x720">HD (1280x720)</option>
                      <option value="1792x1024">Widescreen (1792x1024)</option>
                      <option value="1024x1792">Portrait (1024x1792)</option>
                      <option value="1024x1024">Square (1024x1024)</option>
                    </select>
                  </div>

                  {/* Generate Button */}
                  <button
                    onClick={generateAdVideo}
                    disabled={isGeneratingAd || !adPrompt.trim()}
                    className="w-full py-3 bg-[#a855f7] hover:bg-[#9333ea] text-white font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="generate-ad-btn"
                  >
                    {isGeneratingAd ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generating... (2-5 mins)
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generate AI Video
                      </>
                    )}
                  </button>

                  <p className="font-mono text-xs text-slate-500 mt-4 text-center">
                    Powered by OpenAI Sora 2
                  </p>
                </>
              ) : (
                <>
                  {/* Video Result */}
                  <div className="mb-4">
                    <video
                      src={generatedAdVideo}
                      controls
                      autoPlay
                      loop
                      className="w-full rounded-2xl"
                      data-testid="generated-video"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={downloadGeneratedVideo}
                      className="flex-1 py-3 bg-[#00f2ea] text-[#02040a] font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2"
                    >
                      <Download className="w-5 h-5" />
                      Download
                    </button>
                    <button
                      onClick={() => setGeneratedAdVideo(null)}
                      className="flex-1 py-3 bg-white/10 text-white font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2 hover:bg-white/20"
                    >
                      <RefreshCw className="w-5 h-5" />
                      New Video
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Timelapse Result Modal */}
      <AnimatePresence>
        {timelapseVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#02040a]/90 backdrop-blur-sm"
            onClick={() => setTimelapseVideo(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-panel max-w-2xl w-full p-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="timelapse-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-unbounded font-bold text-xl text-white flex items-center gap-2">
                  <RefreshCw className="w-6 h-6 text-[#a855f7]" />
                  Timelapse Complete
                </h2>
                <button
                  onClick={() => setTimelapseVideo(null)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <video
                src={timelapseVideo}
                controls
                autoPlay
                loop
                className="w-full rounded-2xl mb-4"
              />

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = timelapseVideo;
                    link.download = `aether-lens-timelapse-${Date.now()}.mp4`;
                    link.click();
                    toast.success("Timelapse downloaded!");
                  }}
                  className="flex-1 py-3 bg-[#a855f7] text-white font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Download
                </button>
                <button
                  onClick={() => setTimelapseVideo(null)}
                  className="flex-1 py-3 bg-white/10 text-white font-unbounded font-bold rounded-2xl hover:bg-white/20"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Theme Selector Modal */}
      <ThemeSelector 
        isOpen={showThemeSelector} 
        onClose={() => setShowThemeSelector(false)} 
      />

      {/* Purchase / Commercial Inquiry Modal */}
      <AnimatePresence>
        {showPurchaseModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#02040a]/95 backdrop-blur-md overflow-y-auto"
            onClick={() => setShowPurchaseModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="glass-panel max-w-2xl w-full p-8 my-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="purchase-modal"
              style={{ 
                border: '1px solid rgba(34,197,94,0.3)',
                boxShadow: '0 0 60px rgba(34,197,94,0.1)'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-unbounded font-bold text-2xl text-white flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-xl">
                      <ShoppingCart className="w-6 h-6 text-green-500" />
                    </div>
                    Purchase Skybot
                  </h2>
                  <p className="font-exo2 text-slate-400 mt-1">Commercial licensing & acquisition</p>
                </div>
                <button
                  onClick={() => setShowPurchaseModal(false)}
                  className="text-slate-400 hover:text-white transition-colors p-2"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Pricing Tiers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Cpu className="w-5 h-5 text-cyan-400" />
                    <span className="font-unbounded font-bold text-white">API License</span>
                  </div>
                  <p className="font-mono text-2xl text-cyan-400 mb-2">$299<span className="text-sm text-slate-500">/month</span></p>
                  <p className="font-exo2 text-xs text-slate-500">10,000 AI analyses, Real-time mode, Full API access</p>
                </div>
                
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-5 h-5 text-purple-400" />
                    <span className="font-unbounded font-bold text-white">Enterprise</span>
                  </div>
                  <p className="font-mono text-2xl text-purple-400 mb-2">Custom</p>
                  <p className="font-exo2 text-xs text-slate-500">Unlimited usage, White-label, On-premise option</p>
                </div>
                
                <div className="p-4 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 md:col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    <span className="font-unbounded font-bold text-white">Full Acquisition</span>
                    <span className="px-2 py-0.5 bg-green-500/30 text-green-400 text-xs rounded-full font-bold">MAKE AN OFFER</span>
                  </div>
                  <p className="font-exo2 text-sm text-slate-300">Complete source code, IP rights, technology transfer, training & support</p>
                </div>
              </div>

              {/* Inquiry Form */}
              <div className="space-y-4">
                <h3 className="font-unbounded font-bold text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-green-400" />
                  Submit Inquiry
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-xs text-slate-500 uppercase mb-1 block">Name *</label>
                    <input
                      type="text"
                      value={purchaseInquiry.name}
                      onChange={(e) => setPurchaseInquiry({...purchaseInquiry, name: e.target.value})}
                      placeholder="Your name"
                      className="input-default w-full"
                      data-testid="inquiry-name"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-xs text-slate-500 uppercase mb-1 block">Email *</label>
                    <input
                      type="email"
                      value={purchaseInquiry.email}
                      onChange={(e) => setPurchaseInquiry({...purchaseInquiry, email: e.target.value})}
                      placeholder="your@email.com"
                      className="input-default w-full"
                      data-testid="inquiry-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-xs text-slate-500 uppercase mb-1 block">Company</label>
                    <input
                      type="text"
                      value={purchaseInquiry.company}
                      onChange={(e) => setPurchaseInquiry({...purchaseInquiry, company: e.target.value})}
                      placeholder="Company name (optional)"
                      className="input-default w-full"
                      data-testid="inquiry-company"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-xs text-slate-500 uppercase mb-1 block">Inquiry Type</label>
                    <select
                      value={purchaseInquiry.inquiry_type}
                      onChange={(e) => setPurchaseInquiry({...purchaseInquiry, inquiry_type: e.target.value})}
                      className="input-default w-full"
                      data-testid="inquiry-type"
                    >
                      <option value="license">API License</option>
                      <option value="enterprise">Enterprise License</option>
                      <option value="purchase">Full Purchase/Acquisition</option>
                      <option value="partnership">Partnership</option>
                      <option value="custom">Custom Solution</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="font-mono text-xs text-slate-500 uppercase mb-1 block">Budget Range</label>
                  <select
                    value={purchaseInquiry.budget_range}
                    onChange={(e) => setPurchaseInquiry({...purchaseInquiry, budget_range: e.target.value})}
                    className="input-default w-full"
                    data-testid="inquiry-budget"
                  >
                    <option value="">Select budget range</option>
                    <option value="under_1k">Under $1,000/month</option>
                    <option value="1k_5k">$1,000 - $5,000/month</option>
                    <option value="5k_10k">$5,000 - $10,000/month</option>
                    <option value="10k_plus">$10,000+/month</option>
                    <option value="one_time_10k">One-time: Under $10,000</option>
                    <option value="one_time_50k">One-time: $10,000 - $50,000</option>
                    <option value="one_time_100k">One-time: $50,000 - $100,000</option>
                    <option value="one_time_100k_plus">One-time: $100,000+</option>
                    <option value="discuss">Let&apos;s Discuss</option>
                  </select>
                </div>

                <div>
                  <label className="font-mono text-xs text-slate-500 uppercase mb-1 block">Message *</label>
                  <textarea
                    value={purchaseInquiry.message}
                    onChange={(e) => setPurchaseInquiry({...purchaseInquiry, message: e.target.value})}
                    placeholder="Tell us about your needs, use case, or make an offer..."
                    className="input-default w-full h-24 resize-none"
                    data-testid="inquiry-message"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={submitPurchaseInquiry}
                    disabled={isSubmittingInquiry}
                    className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white font-unbounded font-bold rounded-2xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="submit-inquiry-btn"
                  >
                    {isSubmittingInquiry ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        Submit Inquiry
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowPurchaseModal(false)}
                    className="px-6 py-3 bg-white/10 text-white font-unbounded font-bold rounded-2xl hover:bg-white/20 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-center gap-6 text-xs text-slate-500">
                <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> GPT-5.2 Powered</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> Commercial Ready</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-green-500" /> 24hr Response</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Install Banner */}
      <FloatingInstallBanner />
    </div>
  );
};

export default Dashboard;
